import type { MeasurementSystem } from '../../lib/measurementSystem'
import { formatMeasurement, measurementUnit } from '../../lib/measurement'
import { Measure, Dimensions } from '../Measurement'
import { formatReason, reasonDetail, diagnosticDetail, type EnrichedUnloadedItem } from '../../lib/unloadedItemsPresentation'
import { useState, useEffect, useMemo, useRef, lazy, Suspense, type MouseEvent, type ReactNode } from 'react'
import { convertMeasurement, parseMeasurementUnit } from '../../lib/measurement'
import { diagnosticLabel } from '../../lib/optimizationDiagnosticLabels'
import { getCGAlert, getVehicleWarnings } from '../../lib/vehicleDiagnostics'
import CGDiagnostics, { CGConclusion } from './CGDiagnostics'
import LateralBalanceSection from './LateralBalanceSection'
import { OptimizationRun } from '../../types/optimization'
import type { Item3D } from '../../types/optimization'
import type { OptimizationOutputSummary } from '../../types/optimizationOutput'
import type { Product } from '../../services/product.service'
const AxleOverlay2D = lazy(() => import('./AxleOverlay2D'))
const FloorPressureOverlay = lazy(() => import('./FloorPressureOverlay'))
const ScoreRadarChart = lazy(() => import('./ScoreRadarChart'))
import LoadingPlaybackControls from './LoadingPlaybackControls'
import { DashboardPlaybackControls } from './ViewerDashboard'
const StabilityRecommendationsPanel = lazy(() => import('./StabilityRecommendationsPanel'))
import StabilityIndicatorHelp from './StabilityIndicatorHelp'
import AlertMeasurement from './AlertMeasurement'
import ProductForm from '../ProductForm'
import TabbedOverlayPanel, { TabItem } from './TabbedOverlayPanel'
import type { PlaybackSpeed } from '../../hooks/useLoadingPlayback'
import {
  FunnelIcon,
  ScaleIcon,
  ChartBarIcon,
  ListBulletIcon,
  ExclamationTriangleIcon,
  CheckCircleIcon,
  MagnifyingGlassIcon,
  XMarkIcon,
  ExclamationCircleIcon,
  BeakerIcon,
  TruckIcon,
  XCircleIcon,
  CpuChipIcon,
  ShieldCheckIcon,
  ShieldExclamationIcon,
} from '@heroicons/react/24/outline'
import { Box, CircleGauge, Cuboid, Crosshair, Scale, ShieldHalf, Table, Weight } from 'lucide-react'
import { getFillRateColor, computeLoadedCartonsCount, computeTotalCartonsCount, getBaseItemId } from '../../lib/optimizationUtils'
import { getPalletDisplayLabel } from '../../lib/palletPresets'
import { alertItemIds, cargoDisplayId, resolveAlertCargo } from '../../lib/viewerCargo'
import { cargoLineLabels, presentCargoLines, palletDisplayLabels, unloadedDisplayLabels } from '../../lib/cargoLinePresentation'
import { extractUnloadedItems } from '../../lib/unloadedItemsPresentation'
import { cargoSelection } from '../../lib/cargoSelection'

// ─── Warning Helpers ─────────────────────────────────────────────────────────────

const getWarningIcon = (warning: any) => {
  if (typeof warning === 'string') return ExclamationTriangleIcon

  switch (warning.type) {
    case 'balance':
      return ScaleIcon
    case 'zone_imbalance':
      return BeakerIcon
    case 'stability':
      return ExclamationCircleIcon
    case 'temperature_limited_overlap':
    case 'temperature_product_limited_overlap':
      return BeakerIcon
    default:
      return ExclamationTriangleIcon
  }
}

const formatWarningMessage = (warning: any, system: MeasurementSystem = 'metric'): string => {
  if (typeof warning === 'string') return warning
  // The public v1 alert contract has already selected a safe, human-readable
  // message. Do not re-derive presentation from engine-specific parameters.
  if (warning.parameters) return warning.message || 'Avertissement sans détail.'

  if (warning.type === 'balance') {
    const cg = warning.centerOfGravity
    const cgText = cg ? ` (CG: ${[cg.x, cg.y, cg.z].map(value => formatMeasurement(value, 'mm', measurementUnit(system, 'vehicleLength'))).join(', ')})` : ''
    let message = `${warning.message}${cgText}`

    if (warning.recommendations && warning.recommendations.length > 0) {
      message += '\n• ' + warning.recommendations.join('\n• ')
    }

    return message
  }

  if (warning.type === 'zone_imbalance') {
    const imbalance = warning.imbalance ? (warning.imbalance * 100).toFixed(1) : 'N/A'
    return `${warning.message} (${imbalance}% déséquilibre)`
  }

  if (warning.type === 'temperature_limited_overlap') {
    const item = warning.displayName || warning.itemName || warning.itemCode || warning.itemId || 'Item'
    const ratio = warning.overlapRatio ? (warning.overlapRatio * 100).toFixed(1) : 'N/A'
    const intersectionData = warning.intersection
    const intersectionText = intersectionData
      ? `[${intersectionData.min}°C → ${intersectionData.max}°C]`
      : 'N/A'
    const sourceRange = warning.sourceRange
    const targetRange = warning.targetRange
    const sourceText = sourceRange ? `[${sourceRange.min}°C → ${sourceRange.max}°C]` : 'N/A'
    const targetText = targetRange ? `[${targetRange.min}°C → ${targetRange.max}°C]` : 'N/A'
    const reason = warning.reason || 'Chevauchement partiel de température'
    return `⚠ Compatible sous condition: ${item}\nProduit: ${sourceText} | Espace: ${targetText}\n${reason}\nRatio: ${ratio}% | Intersection: ${intersectionText}`
  }

  if (warning.type === 'temperature_product_limited_overlap') {
    const item1 = warning.displayName1 || warning.item1Name || warning.item1Code || warning.item1Id || 'Item 1'
    const item2 = warning.displayName2 || warning.item2Name || warning.item2Code || warning.item2Id || 'Item 2'
    const ratio = warning.overlapRatio ? (warning.overlapRatio * 100).toFixed(1) : 'N/A'
    const intersectionData = warning.intersection
    const intersectionText = intersectionData
      ? `[${intersectionData.min}°C → ${intersectionData.max}°C]`
      : 'N/A'
    const sourceRange = warning.sourceRange
    const targetRange = warning.targetRange
    const sourceText = sourceRange ? `[${sourceRange.min}°C → ${sourceRange.max}°C]` : 'N/A'
    const targetText = targetRange ? `[${targetRange.min}°C → ${targetRange.max}°C]` : 'N/A'
    const reason = warning.reason || 'Chevauchement partiel de température entre produits'
    if (warning.message) {
      return warning.message
    }
    return `⚠ Compatible sous condition: ${item1} ↔ ${item2}\nProduit A: ${sourceText} | Produit B: ${targetText}\n${reason}\nRatio: ${ratio}% | Intersection: ${intersectionText}`
  }

  if (warning.type === 'load_path_moment_unverified') {
    const grouped = warning.grouped && warning.count ? ` (${warning.count} cas)` : ''
    const ratio = typeof warning.eccentricityRatio === 'number'
      ? ` Excentricité max estimée: ${(warning.eccentricityRatio * 100).toFixed(1)}%.`
      : ''
    const affected = warning.recommendations?.length ? `\n• ${warning.recommendations.join('\n• ')}` : ''
    return `Charge empilée excentrée${grouped}: le colis est supporté, mais le modèle uniforme ne prouve pas complètement l'équilibre des moments.${ratio}\nAction: vérifier les piles hautes ou privilégier un alignement plus centré des colis sur leurs supports.${affected}`
  }

  if (warning.type === 'floor_reaction_eccentric') {
    const grouped = warning.grouped && warning.count ? ` (${warning.count} cas)` : ''
    const ratio = typeof warning.eccentricityRatio === 'number'
      ? ` Excentricité max estimée au plancher: ${(warning.eccentricityRatio * 100).toFixed(1)}%.`
      : ''
    const affected = warning.recommendations?.length ? `\n• ${warning.recommendations.join('\n• ')}` : ''
    return `Réaction au sol excentrée${grouped}: la charge arrive hors du centre de l'empreinte au sol, donc la pression uniforme est approximative.${ratio}\nAction: contrôler la pile concernée ou ajouter/répartir le support si la marchandise ou le plancher est sensible.${affected}`
  }

  let message = warning.message || warning.reason || JSON.stringify(warning)

  if (warning.recommendations && warning.recommendations.length > 0) {
    message += '\n• ' + warning.recommendations.join('\n• ')
  }

  return message
}

const warningGroupTitle = (type: string) => {
  switch (type) {
    case 'load_path_moment_unverified':
      return 'Charges empilées excentrées'
    case 'floor_reaction_eccentric':
      return 'Réactions au sol excentrées'
    default:
      return type || 'Avertissement'
  }
}

const groupWarningsForDisplay = (warnings: any[]) => {
  const groupedTypes = new Set(['load_path_moment_unverified', 'floor_reaction_eccentric'])
  const groups = new Map<string, any[]>()
  const result: any[] = []

  for (const warning of warnings) {
    const type = typeof warning === 'object' ? warning.type : null
    if (!type || !groupedTypes.has(type)) {
      result.push(warning)
      continue
    }
    const bucket = groups.get(type) || []
    bucket.push(warning)
    groups.set(type, bucket)
  }

  for (const [type, bucket] of groups.entries()) {
    const ratios = bucket.map(w => w.eccentricityRatio ?? w.parameters?.eccentricityRatio ?? w.parameters?.eccentricity_ratio)
      .map(Number).filter(value => Number.isFinite(value))
    const maxRatio = ratios.length ? Math.max(...ratios) : undefined
    const affected = [...new Set(bucket.flatMap(alertItemIds))]
    result.unshift({
      type,
      grouped: true,
      count: bucket.length,
      severity: bucket.some(w => w.severity === 'high') ? 'high' : bucket.some(w => w.severity === 'medium') ? 'medium' : 'low',
      eccentricityRatio: maxRatio,
      message: `${warningGroupTitle(type)} (${bucket.length} cas)`,
      item_ids: affected,
    })
  }

  return result
}

const countGroupedWarningsForDisplay = (warnings: any[]) => groupWarningsForDisplay(warnings).length

const getItemVolumeM3 = (item: any): number | null => {
  if (item?.volume != null) {
    try {
      return convertMeasurement(Number(item.volume), parseMeasurementUnit(item.volume_unit || 'm3'), 'm3')
    } catch { /* Fall back to dimensions when the supplied unit is missing or invalid. */ }
  }
  try {
    const unit = parseMeasurementUnit(item?.dimension_unit || 'mm')
    return (['length', 'width', 'height'] as const)
      .map(dimension => convertMeasurement(Number(item?.[dimension]), unit, 'm'))
      .reduce((volume, dimension) => volume * dimension, 1)
  } catch {
    return null
  }
}

function optimizationItemToProduct(item: Item3D): Product {
  return {
    id: item.product_id || item.id,
    name: item.product_name || item.description || item.id,
    description: item.product_description ?? item.description ?? null,
    sku: item.sku || item.id,
    hs_code: item.hs_code || null,
    unit_of_measure: item.unit_of_measure || null,
    weight: item.weight,
    weight_unit: item.weight_unit || 'kg',
    length: item.length,
    width: item.width,
    height: item.height,
    dimension_unit: item.dimension_unit || 'mm',
    volume: item.volume ?? null,
    volume_unit: item.volume_unit || 'm3',
    packaging_type: item.packaging_type || null,
    is_dangerous_goods: item.dangerous_goods ?? item.is_hazardous ?? false,
    dangerous_goods_class: item.adr_class == null ? null : String(item.adr_class),
    un_number: item.un_number || null,
    temperature_controlled: item.temperature_controlled ?? false,
    min_temperature: item.min_temperature ?? null,
    max_temperature: item.max_temperature ?? null,
    temperature_unit: item.temperature_unit || 'C',
    country_of_origin: item.country_of_origin || null,
    status: item.status || 'active',
    quantity: item.quantity ?? 1,
    stackable: item.stackable,
    fragile: item.fragile,
    rotatable: item.rotatable,
    no_stack_above: item.no_stack_above,
    center_of_gravity_x: item.center_of_gravity_x ?? null,
    center_of_gravity_y: item.center_of_gravity_y ?? null,
    center_of_gravity_z: item.center_of_gravity_z ?? null,
    max_stack_weight: item.max_stack_weight ?? null,
    max_stack_layer: item.max_stack_layer ?? null,
    color: item.color || '#3b82f6',
    unloading_order: item.unloading_order ?? null,
    keep_upright: item.keep_upright,
    must_be_on_floor: item.must_be_on_floor,
    keep_together_group: item.keep_together_group,
    min_support_ratio: item.min_support_ratio ?? null,
    requires_pallet: item.requires_pallet,
    pallet_type: item.pallet_type || null,
    max_pallet_height_mm: item.max_pallet_height_mm ?? null,
    pallet_custom_length: item.pallet_custom_length ?? null,
    pallet_custom_width: item.pallet_custom_width ?? null,
    max_items_per_pallet: item.max_items_per_pallet ?? null,
    pallet_board_height_mm: item.pallet_board_height_mm ?? null,
    pallet_empty_weight_kg: item.pallet_empty_weight_kg ?? null,
    pallet_color: item.pallet_color || null,
    max_pallet_weight_kg: item.max_pallet_weight_kg ?? null,
    allow_rotation_x: item.rotationOptions?.allowRotationX,
    allow_rotation_y: item.rotationOptions?.allowRotationY,
    allow_rotation_z: item.rotationOptions?.allowRotationZ,
    rotation_strategy: item.rotationOptions?.rotationStrategy || null,
    favorite_rating: item.favorite_rating ?? 0,
  }
}

interface OverlayManagerProps {
  editorPanel?: ReactNode
  editorToolbar?: ReactNode
  editing?: boolean
  presentation?: 'floating' | 'dashboard'
  scene?: ReactNode
  sceneOnly?: boolean
  diagnosticsPanelRequest?: number
  run: OptimizationRun
  viewerRun?: OptimizationRun
  onLocateItems?: (ids: string[]) => void
  outputSummary?: OptimizationOutputSummary
  outputSummaryUnavailable?: boolean
  onCellClick?: (cell: any) => void
  onHotSpotClick?: (hotSpot: any) => void
  fullscreen?: boolean
  runId?: string
  selectedItemIds?: string[]
  setSelectedItemIds?: (ids: string[]) => void
  selectedPressureZone?: {
    x: number
    y: number
    width: number
    length: number
    itemIds: string[]
  } | null
  setSelectedPressureZone?: (zone: any) => void
  selectedRecommendation?: any
  setSelectedRecommendation?: (rec: any) => void
  playbackStep?: number
  playback?: {
    step: number
    totalSteps: number
    isPlaying: boolean
    speed: PlaybackSpeed
    isAtEnd: boolean
    isAtStart: boolean
    progress: number
    currentItemLabel?: string
    onPlay: () => void
    onPause: () => void
    onReset: () => void
    onNext: () => void
    onPrev: () => void
    onSpeedChange: (speed: PlaybackSpeed) => void
    onSeek: (step: number) => void
  }
  navigate?: ReturnType<typeof import('react-router-dom').useNavigate>
  isDark?: boolean
  cg?: { x: number; y: number; z: number } | null
  targetCg?: { x: number; y: number; z: number; source?: string } | null
  showRealCG?: boolean
  showTargetCG?: boolean
  onShowRealCGChange?: (show: boolean) => void
  onShowTargetCGChange?: (show: boolean) => void
  unloadedItems?: EnrichedUnloadedItem[]
  fillRateWeight?: number | null
  currentView?: 'default' | 'top' | 'front' | 'side'
  onViewChange?: (view: 'default' | 'top' | 'front' | 'side') => void
  searchQuery?: string
  setSearchQuery?: (query: string) => void
  articlesTab?: 'items' | 'pallets'
  setArticlesTab?: (tab: 'items' | 'pallets') => void
  validPlacementCounts?: Record<string, number>
  validPalletGroups?: Array<any>
  openWarningsPanel?: boolean | number
  violationsPanelRequest?: number
  unloadedPanelRequest?: number
  pressurePanelRequest?: number
}

const alertSeverity = (severity?: string) => ({ critical: 'Critique', error: 'Erreur', high: 'Élevée', warning: 'Avertissement', medium: 'Modérée', low: 'Faible', info: 'Information' }[severity ?? 'warning'] ?? severity)

export default function OverlayManager({
  presentation = 'floating', scene, sceneOnly = false, diagnosticsPanelRequest = 0, editorPanel, editorToolbar, editing = false,
  run: sourceRun,
  viewerRun: sourceViewerRun,
  onLocateItems,
  outputSummary: sourceSummary,
  outputSummaryUnavailable = false,
  onCellClick,
  onHotSpotClick,
  fullscreen = false,
  selectedItemIds: _selectedItemIds = [],
  setSelectedItemIds: _setSelectedItemIds,
  selectedPressureZone: _selectedPressureZone,
  setSelectedPressureZone: _setSelectedPressureZone,
  playbackStep: _playbackStep,
  playback,
  navigate: _navigate,
  isDark = false,
  cg,
  targetCg,
  showRealCG = true,
  showTargetCG = false,
  onShowRealCGChange,
  onShowTargetCGChange,
  unloadedItems: sourceUnloadedItems = [],
  fillRateWeight = null,
  searchQuery = '',
  setSearchQuery,
  articlesTab: _articlesTab = 'items',
  setArticlesTab: _setArticlesTab,
  validPlacementCounts = {},
  validPalletGroups = [],
  openWarningsPanel = false,
  pressurePanelRequest = 0,
  violationsPanelRequest = 0,
  unloadedPanelRequest = 0,
  setSelectedRecommendation: _setSelectedRecommendation,
}: OverlayManagerProps) {
  const { run, summary: outputSummary } = useMemo(() => presentCargoLines(sourceRun, sourceSummary), [sourceRun, sourceSummary])
  const viewerRun = sourceViewerRun ?? run
  const unloadedItems = sourceRun === run ? sourceUnloadedItems : extractUnloadedItems(run)
  const selectionAnchor = useRef<{ items?: string; pallets?: string }>({})
  useEffect(() => { if (!_selectedItemIds.length) selectionAnchor.current = {} }, [_selectedItemIds])
  // Tab states
  const [activeTab, setActiveTab] = useState<string>('articles')
  const [activeSubTab, setActiveSubTab] = useState<Record<string, string>>({
    articles: 'items',
    axle_cg: 'axle',
    cg: 'cg_deviation',
    score_metrics: 'score',
    stability: 'indicators',
    alerts: 'violations',
  })
  const [isPanelOpen, setIsPanelOpen] = useState<boolean>(false)
  const [unloadedSearchQuery, setUnloadedSearchQuery] = useState<string>('')
  const [productFormItem, setProductFormItem] = useState<Product | null>(null)
  useEffect(() => { if (diagnosticsPanelRequest > 0) { setActiveTab('diagnostics'); setIsPanelOpen(true) } }, [diagnosticsPanelRequest])
  useEffect(() => {
    if (unloadedPanelRequest > 0) {
      setActiveTab('articles')
      setActiveSubTab(prev => ({ ...prev, articles: 'unloaded' }))
      setIsPanelOpen(true)
    }
  }, [unloadedPanelRequest])

  useEffect(() => {
    if (pressurePanelRequest > 0) {
      setActiveTab('pressure')
      setIsPanelOpen(true)
    }
  }, [pressurePanelRequest])

  // Keyboard shortcuts
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (editing || e.defaultPrevented || document.querySelector('dialog[open]') || (e.target as HTMLElement).closest('input, textarea, select, [contenteditable="true"]')) {
        return
      }

      switch (e.key.toLowerCase()) {
        case '1':
          setActiveTab('axle_cg')
          setActiveSubTab(prev => ({ ...prev, axle_cg: 'axle' }))
          setIsPanelOpen(true)
          break
        case '2':
          setActiveTab('pressure')
          setIsPanelOpen(true)
          break
        case '3':
          setActiveTab('score_metrics')
          setActiveSubTab(prev => ({ ...prev, score_metrics: 'score' }))
          setIsPanelOpen(true)
          break
        case '4':
          setActiveTab('stability')
          setIsPanelOpen(true)
          break
        case '6':
          setActiveTab('cg')
          setIsPanelOpen(true)
          break
        case '7':
          setActiveTab('articles')
          setActiveSubTab(prev => ({ ...prev, articles: 'unloaded' }))
          setIsPanelOpen(true)
          break
        case '8':
          setActiveTab('score_metrics')
          setActiveSubTab(prev => ({ ...prev, score_metrics: 'metrics' }))
          setIsPanelOpen(true)
          break
        case 'l':
          setActiveTab('articles')
          setActiveSubTab(prev => ({ ...prev, articles: 'items' }))
          setIsPanelOpen(true)
          break
        case 'r':
          setActiveTab('axle_cg')
          setActiveSubTab(prev => ({ ...prev, axle_cg: 'recommendations' }))
          setIsPanelOpen(true)
          break
        case 'w':
          setActiveTab('alerts')
          setActiveSubTab(prev => ({ ...prev, alerts: 'warnings' }))
          setIsPanelOpen(true)
          break
        case 'v':
          setActiveTab('alerts')
          setActiveSubTab(prev => ({ ...prev, alerts: 'violations' }))
          setIsPanelOpen(true)
          break
      }
    }

    window.addEventListener('keydown', handleKeyDown)
    return () => window.removeEventListener('keydown', handleKeyDown)
  }, [editing])

  // Open warnings panel when openWarningsPanel prop changes to true
  useEffect(() => {
    if (openWarningsPanel) {
      setActiveTab('alerts')
      setActiveSubTab(prev => ({ ...prev, alerts: 'warnings' }))
      setIsPanelOpen(true)
    }
  }, [openWarningsPanel])

  useEffect(() => {
    if (violationsPanelRequest) {
      setActiveTab('alerts')
      setActiveSubTab(prev => ({ ...prev, alerts: 'violations' }))
      setIsPanelOpen(true)
    }
  }, [violationsPanelRequest])

  if (!run.result) return null

  // Computed data
  const loadedCartonsCount = outputSummary ? outputSummary.cargo.loaded : computeLoadedCartonsCount(run)
  const totalCartonsCount = outputSummary?.cargo.requested ?? computeTotalCartonsCount(run)
  const contractAlerts = outputSummary?.alerts
  const violations = contractAlerts
    ? contractAlerts.violations.map((alert, index) => ({
      id: `contract-violation-${index}`, type: alert.type, severity: alert.severity,
      item_id: alert.item_ids[0], item_ids: alert.item_ids, parameters: alert.parameters, measurement: alert.measurement, message: alert.message || 'Violation sans détail.'
    })) as any[]
    : run.result?.violations || []
  const warnings = contractAlerts
    ? contractAlerts.warnings.map(alert => ({ type: alert.type, severity: alert.severity, message: alert.message, item_ids: alert.item_ids, parameters: alert.parameters, measurement: alert.measurement }))
    : getVehicleWarnings(run.result)
  const cgAlert = getCGAlert(run.result?.advancedAxleAnalysis)
  const groupedWarningCount = countGroupedWarningsForDisplay(warnings)
  const unloadedCount = unloadedItems.reduce((count, item) => count + (item.quantity ?? 1), 0)
  const loadPathWarnings = warnings.filter(w => typeof w === 'object' && w.type === 'load_path_moment_unverified')
  const floorReactionWarnings = warnings.filter(w => typeof w === 'object' && w.type === 'floor_reaction_eccentric')
  const globalScore = outputSummaryUnavailable ? null : outputSummary
    ? outputSummary.metrics.global_score == null ? null : outputSummary.metrics.global_score * 100
    : run.result?.global_score == null ? null : run.result.global_score * 100
  const balanceWarning = warnings.find(w => typeof w === 'object' && w.type === 'balance')
  const axleMassWarning = getVehicleWarnings(run.result).some(warning => warning.type === 'axle_mass_inconsistent')
  const physicalAxles = outputSummary?.physical_analysis?.axles
  const axleCompliance = axleMassWarning ? null : physicalAxles && physicalAxles.status !== 'unavailable'
    ? physicalAxles.summary?.status === 'pass' ? true
      : physicalAxles.summary?.status === 'fail' ? false : null
    : run.result?.advancedAxleAnalysis?.axleSummary?.isCompliant ?? null
  const pressureContract = outputSummary?.physical_analysis?.floor_pressure
  const legacyFloorPressure = run.result?.floorPressure
  const pressureAssessment = pressureContract
    ? pressureContract.status === 'pass' ? 'within_limit'
      : pressureContract.status === 'fail' ? 'exceeded' : 'unverified'
    : legacyFloorPressure?.assessment
  const pressureItemsOverLimit = pressureContract?.items_over_limit
    ?? legacyFloorPressure?.itemsOverLimit
    ?? null
  const pressureValidity = pressureContract
    ? pressureContract.status === 'pass' ? true : pressureContract.status === 'fail' ? false : null
    : legacyFloorPressure?.isValid ?? null
  const pressureIsOkay = pressureAssessment === 'unverified' ? undefined
    : pressureAssessment === 'exceeded' || (pressureItemsOverLimit ?? 0) > 0 ? false
      : pressureAssessment === 'within_limit' ? true
        : pressureValidity !== null ? pressureValidity : undefined
  const pressurePolicy = pressureContract?.policy ?? run.input?.constraints?.floorPressurePolicy ?? 'strict'

  // Stability computations
  // V09 : normalisation des alias snake_case / camelCase
  const contractStability = outputSummary?.stability_analysis
  const ds = contractStability ? {
    isStable: contractStability.overall.status === 'pass' ? true : contractStability.overall.status === 'fail' ? false : null,
    overallScore: contractStability.overall.score_fraction,
    overallRiskLevel: contractStability.overall.risk_level ?? 'unknown',
    rolloverIndex: contractStability.rollover.index,
    rolloverRiskLevel: contractStability.rollover.risk_level ?? 'unknown',
    yawRiskFactor: contractStability.yaw.risk_factor,
    rollStabilityRatio: contractStability.roll.stability_ratio,
  } : run.result?.dynamicStability
    ?? run.result?.dynamic_stability
    ?? (() => {
      const dsd = run.result?.scoreBreakdown?.dynamicStabilityDetail
      if (!dsd) return null
      const overallRiskLevel = dsd.metadata?.severity ?? 'unknown'
      return {
        isStable: dsd.rollover?.isStable && dsd.yaw?.isStable && dsd.roll?.isStable,
        overallScore: (dsd.overallScore ?? 0) / 100,
        overallRiskLevel,
        rolloverIndex: dsd.rollover?.rolloverIndex ?? null,
        rolloverRiskLevel: dsd.rollover?.riskLevel ?? 'unknown',
        yawRiskFactor: dsd.yaw?.yawRiskFactor ?? null,
        rollStabilityRatio: dsd.roll?.rollStabilityRatio ?? null,
      }
    })()

  // ── Tab 1: Articles Content ──
  const items = run.input.items || []
  const itemLabels = cargoLineLabels(items)
  const palletLabels = palletDisplayLabels(run)
  const unloadedLabels = unloadedDisplayLabels(unloadedItems)
  const selectionLabels = _selectedItemIds.map(id => itemLabels.get(id) ?? palletLabels.get(id) ?? cargoDisplayId(id))
  const inputItemIds = new Set(items.map(item => item.id))

  const filteredItems = items.filter(item => {
    const q = (searchQuery || '').toLowerCase()
    return (
      item.id.toLowerCase().includes(q) ||
      (itemLabels.get(item.id) || '').toLowerCase().includes(q) ||
      (item.description && item.description.toLowerCase().includes(q)) ||
      (item.keep_together_group && item.keep_together_group.toLowerCase().includes(q))
    )
  })

  const filteredPalletGroups = validPalletGroups.filter((pg: any) => {
    const q = (searchQuery || '').toLowerCase()
    return (
      pg.pallet_id.toLowerCase().includes(q) ||
      (palletLabels.get(pg.pallet_id) || '').toLowerCase().includes(q) ||
      (pg.children_item_ids || []).some((id: string) => id.toLowerCase().includes(q))
    )
  })

  const filteredUnloadedItems = unloadedItems.filter((item: any) => {
    const q = (unloadedSearchQuery || '').toLowerCase()
    return (
      item.id.toLowerCase().includes(q) ||
      (item.description && item.description.toLowerCase().includes(q)) ||
      (unloadedLabels.get(item) || '').toLowerCase().includes(q)
    )
  })

  const validPlacements = run.result.placements || []
  const loadedItemIds = new Set(validPlacements.map(p => p.item_id || p.itemId || ''))
  const loadedPalletIds = new Set(
    validPalletGroups
      .filter((pg: any) => loadedItemIds.has(pg.pallet_id))
      .map((pg: any) => pg.pallet_id)
  )
  const toggleSelectedItem = (id: string, event: MouseEvent, tab: 'items' | 'pallets') => {
    const ordered = tab === 'items' ? filteredItems.map(item => item.id) : filteredPalletGroups.map(group => group.pallet_id)
    const selection = cargoSelection(_selectedItemIds, id, ordered, selectionAnchor.current[tab], event)
    selectionAnchor.current[tab] = selection.anchor
    _setSelectedItemIds?.(selection.ids)
  }
  const isSelectedItemId = (id: string) => _selectedItemIds.some(selectedId =>
    id === selectedId || id.startsWith(`${selectedId}_`)
  )

  const articlesItemsContent = () => (
    <div className="flex min-h-0 min-w-0 w-full flex-1 flex-col gap-3">
      {/* Search Input */}
      <div className="relative">
        <span className="absolute inset-y-0 left-0 flex items-center pl-3 pointer-events-none text-gray-400">
          <MagnifyingGlassIcon className="w-4 h-4" />
        </span>
        <input
          type="text"
          value={searchQuery}
          onChange={(e) => setSearchQuery?.(e.target.value)}
          aria-label="Rechercher un article"
          placeholder="Rechercher un article..."
          className="w-full pl-9 pr-8 py-2 text-xs bg-gray-50 dark:bg-gray-900/60 border border-gray-200 dark:border-gray-800 rounded-xl text-gray-900 dark:text-white placeholder-gray-400 dark:placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-orange-500/20 focus:border-orange-500 transition-all duration-200"
        />
        {searchQuery && (
          <button
            onClick={() => setSearchQuery?.('')}
            aria-label="Effacer la recherche"
            className="absolute inset-y-0 right-0 flex items-center pr-2.5 text-gray-400 hover:text-gray-200 transition-colors"
          >
            <XMarkIcon className="w-3.5 h-3.5" />
          </button>
        )}
      </div>

      {/* Active Filter Bar */}
      {_selectedItemIds.length > 0 && (
        <div className="px-3 py-2 bg-blue-500/10 border border-blue-500/20 rounded-xl flex items-center justify-between text-xs">
          <span className="text-orange-800 dark:text-orange-300 flex items-center gap-1.5">
            <FunnelIcon className="w-3.5 h-3.5 text-blue-400 animate-pulse" />
            Sélection ({_selectedItemIds.length}) : <strong className="text-slate-900 dark:text-white font-semibold truncate">{selectionLabels.join(', ')}</strong>
          </span>
          <button
            onClick={() => _setSelectedItemIds?.([])}
            className="text-xs font-semibold text-orange-700 dark:text-orange-400 hover:underline"
          >
            Effacer
          </button>
        </div>
      )}

      {/* Items list */}
      <div className="min-h-0 flex-1 space-y-1.5 overflow-y-auto pr-1">
        {filteredItems.length > 0 ? (
          filteredItems.map((item) => {
            const countsKnown = (item as any)._viewerLoaded !== null
            const loadedCount = (item as any)._viewerLoaded ?? validPlacementCounts[item.id] ?? 0
            const totalCount = item.quantity || 1
            const loadPercent = Math.min(100, Math.round((loadedCount / totalCount) * 100))
            const isSelected = _selectedItemIds.includes(item.id)
            const isUnloaded = countsKnown && loadedCount === 0
            const isPartiallyLoaded = countsKnown && loadedCount > 0 && loadedCount < totalCount
            const colorStyle = item.color || '#3b82f6'
            const volumeM3 = getItemVolumeM3(item)

            let badgeBg = 'bg-red-50 text-red-700 dark:bg-red-950/30 dark:text-red-400 border border-red-200/50 dark:border-red-900/30'
            let barColor = 'bg-red-500'
            if (loadedCount >= totalCount) {
              badgeBg = 'bg-green-50 text-green-700 dark:bg-green-950/30 dark:text-green-400 border border-green-200/50 dark:border-green-900/30'
              barColor = 'bg-green-500'
            } else if (loadedCount > 0) {
              badgeBg = 'bg-yellow-50 text-yellow-700 dark:bg-yellow-950/30 dark:text-yellow-400 border border-yellow-200/50 dark:border-yellow-900/30'
              barColor = 'bg-yellow-500'
            }

            return (
              <div key={item.id} className="relative">
              <button
                type="button"
                aria-pressed={isSelected}
                onClick={event => toggleSelectedItem(item.id, event, 'items')}
                className={`w-full text-left pl-10 pr-3 py-3 rounded-xl border transition-all duration-200 text-xs flex flex-col gap-2 focus:outline-none ${isSelected
                  ? isDark
                    ? 'bg-gradient-to-br from-blue-950/40 to-blue-900/20 border-blue-500/80 ring-2 ring-blue-500/20'
                    : 'bg-blue-50 border-blue-500 ring-2 ring-blue-500/20 shadow-sm'
                  : isDark
                    ? 'bg-white/[0.03] border-white/[0.08] hover:border-white/[0.15] hover:bg-white/[0.06]'
                    : 'bg-white border-slate-200 hover:border-slate-300 hover:bg-slate-50 shadow-sm'
                  }`}
              >
                <div className="flex items-start justify-between gap-2 w-full">
                  <div className="flex items-center gap-2.5 min-w-0">
                    <div className="min-w-0">
                      <h4 className={`font-bold truncate ${isDark ? 'text-slate-100' : 'text-slate-900'}`}>{itemLabels.get(item.id)}</h4>
                    </div>
                  </div>
                  <div className="flex items-center gap-1.5">
                    {item.requires_pallet && (
                      <span title="Palettisation automatique" className="flex-shrink-0">
                        <Table aria-label="Palettisation automatique" className="w-3.5 h-3.5 text-emerald-500" />
                      </span>
                    )}
                    {isUnloaded && (
                      <span className="text-[9px] px-2 py-0.5 rounded-full font-bold bg-red-50 text-red-700 border border-red-200 dark:bg-red-900/50 dark:text-red-300 dark:border-red-800">
                        Non chargé
                      </span>
                    )}
                    {isPartiallyLoaded && (
                      <span className="text-[9px] px-2 py-0.5 rounded-full font-bold bg-orange-50 text-orange-700 border border-orange-200 dark:bg-orange-900/50 dark:text-orange-300 dark:border-orange-800">
                        Partiel
                      </span>
                    )}
                    <span className={`text-[10px] px-2 py-0.5 rounded-full font-bold ${badgeBg}`}>
                      {countsKnown ? loadedCount : '—'}/{totalCount}
                    </span>
                  </div>
                </div>

                <div className={`flex flex-wrap items-center gap-x-2.5 gap-y-1 text-[10px] font-medium ${isDark ? 'text-slate-400' : 'text-slate-700'}`}>
                  <span><Dimensions length={item.length} width={item.width} height={item.height} from={item.dimension_unit || 'mm'} /></span>
                  <span className="inline-flex items-center gap-1"><Weight className="w-3 h-3" aria-hidden="true" /><Measure value={item.weight} from={item.weight_unit || 'kg'} quantity="mass" /></span>
                  {volumeM3 != null && <span className="inline-flex items-center gap-1"><Box className="w-3 h-3" aria-hidden="true" /><Measure value={volumeM3} from="m3" quantity="volume" /></span>}
                  {item.fragile && <span className="text-red-500 font-bold">Fragile</span>}
                  {item.dangerous_goods && <span className="text-amber-500 font-bold">ADR</span>}
                </div>

                <div className="w-full">
                  {!countsKnown && <p className="mb-1 text-amber-600">Quantité chargée non déterminable : lignes de cargaison ambiguës dans ce résultat.</p>}
                  <div className={`w-full h-1.5 rounded-full overflow-hidden ${isDark ? 'bg-slate-800' : 'bg-slate-200'}`}>
                    <div
                      className={`${barColor} h-full transition-all duration-500 rounded-full`}
                      style={{ width: `${loadPercent}%` }}
                    />
                  </div>
                </div>
              </button>
              <button
                type="button"
                title={`Afficher la fiche de saisie de ${itemLabels.get(item.id)}`}
                aria-label={`Afficher la fiche de saisie de ${itemLabels.get(item.id)}`}
                onClick={() => setProductFormItem(optimizationItemToProduct(item))}
                className={`absolute left-2 top-3 z-10 flex h-6 w-6 items-center justify-center rounded-md ${isDark ? 'text-slate-400 hover:bg-slate-700 hover:text-white' : 'text-slate-500 hover:bg-blue-50 hover:text-blue-700'}`}
              >
                <span className="relative inline-flex h-4 w-4 flex-shrink-0" aria-hidden="true">
                  <Cuboid className="absolute inset-0 h-full w-full" fill={colorStyle} stroke="none" />
                  <Cuboid className="absolute inset-0 h-full w-full" fill="none" color="#fff" />
                </span>
              </button>
              </div>
            )
          })
        ) : (
          <div className={`text-center py-8 text-xs ${isDark ? 'text-slate-400' : 'text-slate-600'}`}>
            Aucun article trouvé
          </div>
        )}
      </div>
    </div>
  )

  const articlesPalletsContent = () => (
    <div className="flex min-h-0 min-w-0 w-full flex-1 flex-col gap-3">
      <p className="text-xs" data-testid="pallet-summary">Palettes : {outputSummary?.pallets?.loaded ?? loadedPalletIds.size} chargées / {outputSummary?.pallets?.generated ?? validPalletGroups.length} générées · {outputSummary?.pallets?.remaining ?? validPalletGroups.length - loadedPalletIds.size} non chargées</p>
      {/* Search Input */}
      <div className="relative">
        <span className="absolute inset-y-0 left-0 flex items-center pl-3 pointer-events-none text-gray-400">
          <MagnifyingGlassIcon className="w-4 h-4" />
        </span>
        <input
          type="text"
          value={searchQuery}
          onChange={(e) => setSearchQuery?.(e.target.value)}
          aria-label="Rechercher une palette"
          placeholder="Rechercher une palette..."
          className="w-full pl-9 pr-8 py-2 text-xs bg-gray-50 dark:bg-gray-900/60 border border-gray-200 dark:border-gray-800 rounded-xl text-gray-900 dark:text-white placeholder-gray-400 dark:placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-orange-500/20 focus:border-orange-500 transition-all duration-200"
        />
        {searchQuery && (
          <button
            onClick={() => setSearchQuery?.('')}
            aria-label="Effacer la recherche"
            className="absolute inset-y-0 right-0 flex items-center pr-2.5 text-gray-400 hover:text-gray-200 transition-colors"
          >
            <XMarkIcon className="w-3.5 h-3.5" />
          </button>
        )}
      </div>

      {/* Active Filter Bar */}
      {_selectedItemIds.length > 0 && (
        <div className="px-3 py-2 bg-blue-500/10 border border-blue-500/20 rounded-xl flex items-center justify-between text-xs">
          <span className="text-orange-800 dark:text-orange-300 flex items-center gap-1.5">
            <FunnelIcon className="w-3.5 h-3.5 text-blue-400 animate-pulse" />
            Sélection ({_selectedItemIds.length}) : <strong className="font-semibold truncate">{selectionLabels.join(', ')}</strong>
          </span>
          <button
            onClick={() => _setSelectedItemIds?.([])}
            className="text-xs font-semibold text-orange-700 dark:text-orange-400 hover:underline"
          >
            Effacer
          </button>
        </div>
      )}

      {/* Pallets list */}
      <div className="min-h-0 flex-1 space-y-1.5 overflow-y-auto pr-1">
        {filteredPalletGroups.length > 0 ? (
          filteredPalletGroups.map((pg: any) => {
            const isSelected = _selectedItemIds.includes(pg.pallet_id) ||
              (pg.children_positions || []).some((child: any) => _selectedItemIds.includes(child.source_id)) ||
              (pg.children_item_ids || []).some((id: string) => isSelectedItemId(id))
            const isLoaded = loadedPalletIds.has(pg.pallet_id)
            const childCargoVolumeM3 = Array.isArray(pg.children_positions) && pg.children_positions.length > 0
              ? pg.children_positions.reduce((total: number, child: any) => total + (child.length * child.width * child.height) / 1_000_000_000, 0)
              : (pg.children_item_ids || []).reduce((total: number, id: string) => {
                const childItem = items.find((candidate: any) => id === candidate.id || id.startsWith(`${candidate.id}_`))
                return total + (getItemVolumeM3(childItem) ?? 0)
              }, 0)
            const palletVolumeM3 = pg.pallet_dims?.length && pg.pallet_dims?.width && pg.pallet_dims?.height
              ? (pg.pallet_dims.length * pg.pallet_dims.width * pg.pallet_dims.height) / 1_000_000_000
              : null
            const palletTypeLabel = getPalletDisplayLabel(pg.pallet_type || 'CUSTOM', pg.pallet_dims)

            const baseChildId = pg.children_positions?.[0]?.source_id ?? pg.children_item_ids?.[0]
            const childItem = baseChildId ? items.find((i: any) => i.id === baseChildId || (baseChildId.includes('_') && !isNaN(Number(baseChildId.split('_').pop())) && i.id === baseChildId.split('_').slice(0, -1).join('_'))) : null
            const childColor = childItem?.color

            return (
              <button
                key={pg.pallet_id}
                type="button"
                aria-pressed={isSelected}
                onClick={event => toggleSelectedItem(pg.pallet_id, event, 'pallets')}
                className={`w-full text-left p-3 rounded-xl border transition-all duration-200 text-xs flex flex-col gap-2 focus:outline-none ${isSelected
                  ? isDark
                    ? 'bg-gradient-to-br from-blue-950/40 to-blue-900/20 border-blue-500/80 ring-2 ring-blue-500/20'
                    : 'bg-blue-50 border-blue-500 ring-2 ring-blue-500/20 shadow-sm'
                  : isDark
                    ? 'bg-white/[0.03] border-white/[0.08] hover:border-white/[0.15] hover:bg-white/[0.06]'
                    : 'bg-white border-slate-200 hover:border-slate-300 hover:bg-slate-50 shadow-sm'
                  }`}
              >
                <div className="flex items-start justify-between gap-2 w-full">
                  <div className="flex items-center gap-2.5 min-w-0">
                    <Table className="w-4 h-4 flex-shrink-0" style={{
                      color: childColor || (isDark ? '#fbbf24' : '#b45309'),
                    }} />
                    <div className="min-w-0">
                      <h4 className={`font-bold truncate ${isDark ? 'text-slate-100' : 'text-slate-900'}`}>{palletLabels.get(pg.pallet_id)}</h4>
                      <p className={`text-[10px] truncate mt-0.5 ${isDark ? 'text-slate-400' : 'text-slate-600 font-medium'}`}>{palletTypeLabel}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-1.5">
                    {!isLoaded && (
                      <span className={`text-[9px] px-2 py-0.5 rounded-full font-bold ${isDark
                        ? 'bg-red-50 text-red-700 border border-red-200 dark:bg-red-900/50 dark:text-red-300 dark:border-red-800'
                        : 'bg-red-50 text-red-700 border border-red-200'
                        }`}>
                        Non chargé
                      </span>
                    )}
                    <span className="text-[10px] px-2 py-0.5 rounded-full font-bold border" style={{
                      backgroundColor: childColor ? `${childColor}${isDark ? '33' : '1A'}` : (isDark ? 'rgba(245,158,11,0.2)' : 'rgba(254,243,199,1)'),
                      color: childColor || (isDark ? '#fcd34d' : '#92400e'),
                      borderColor: childColor ? `${childColor}50` : (isDark ? 'rgba(245,158,11,0.3)' : 'rgba(253,230,138,1)'),
                    }}>
                      {pg.children_item_ids.length} colis
                    </span>
                  </div>
                </div>

                <div className={`flex flex-wrap items-center gap-x-2.5 gap-y-1 text-[10px] font-medium ${isDark ? 'text-slate-400' : 'text-slate-700'}`}>
                  <span><Dimensions length={pg.pallet_dims.length} width={pg.pallet_dims.width} height={pg.pallet_dims.height} /></span>
                  <span className="inline-flex items-center gap-1"><Weight className="w-3 h-3" aria-hidden="true" /><Measure value={pg.pallet_weight_kg} from="kg" quantity="mass" /></span>
                  {childCargoVolumeM3 > 0 && <span className="inline-flex items-center gap-1"><Box className="w-3 h-3" aria-hidden="true" />Colis <Measure value={childCargoVolumeM3} from="m3" quantity="volume" /></span>}
                  {palletVolumeM3 != null && <span className="inline-flex items-center gap-1"><Table className="w-3 h-3" aria-hidden="true" />Palette <Measure value={palletVolumeM3} from="m3" quantity="volume" /></span>}
                </div>
              </button>
            )
          })
        ) : (
          <div className={`text-center py-8 text-xs ${isDark ? 'text-slate-400' : 'text-slate-600 font-medium'}`}>
            {validPalletGroups.length === 0 ? 'Aucune palette dans ce chargement' : 'Aucune palette trouvée'}
          </div>
        )}
      </div>
    </div>
  )

  const unloadedItemsContent = () => (
    <div className="flex min-h-0 min-w-0 w-full flex-1 flex-col gap-3">
      {/* Search Input */}
      <div className="relative">
        <span className="absolute inset-y-0 left-0 flex items-center pl-3 pointer-events-none text-gray-400">
          <MagnifyingGlassIcon className="w-4 h-4" />
        </span>
        <input
          type="text"
          value={unloadedSearchQuery}
          onChange={(e) => setUnloadedSearchQuery(e.target.value)}
          aria-label="Rechercher un colis non chargé"
          placeholder="Rechercher un colis non chargé..."
          className="w-full pl-9 pr-8 py-2 text-xs bg-gray-50 dark:bg-gray-900/60 border border-gray-200 dark:border-gray-800 rounded-xl text-gray-900 dark:text-white placeholder-gray-400 dark:placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-orange-500/20 focus:border-orange-500 transition-all duration-200"
        />
        {unloadedSearchQuery && (
          <button
            onClick={() => setUnloadedSearchQuery('')}
            className="absolute inset-y-0 right-0 flex items-center pr-2.5 text-gray-400 hover:text-gray-200 transition-colors"
          >
            <XMarkIcon className="w-3.5 h-3.5" />
          </button>
        )}
      </div>

      {/* Unloaded items list */}
      <div className="flex min-h-0 flex-1 flex-col gap-2 overflow-y-auto pr-1">
        {filteredUnloadedItems.length > 0 ? filteredUnloadedItems.map(item => {
          const colorStyle = item.color || '#3b82f6'
          const sourceItemId = getBaseItemId(item.id, inputItemIds, (item as any).source_id || (item as any).reference)
          const sourceItem = items.find(inputItem => inputItem.id === sourceItemId)
          return (
            <div
              key={`${sourceItemId}:${item.id}`}
              className={`rounded-xl p-3 text-xs border transition-all duration-200 ${isDark
                ? 'bg-red-500/10 border-red-500/20'
                : 'bg-red-50/80 border-red-200 shadow-sm'
                }`}
            >
              <div className="flex items-start justify-between gap-2">
                <div className="flex items-center gap-2.5 min-w-0">
                  <button
                    type="button"
                    disabled={!sourceItem}
                    title={sourceItem ? `Afficher la fiche de saisie de ${unloadedLabels.get(item)}` : 'Article source indisponible'}
                    aria-label={sourceItem ? `Afficher la fiche de saisie de ${unloadedLabels.get(item)}` : `Fiche indisponible pour ${unloadedLabels.get(item)}`}
                    onClick={() => sourceItem && setProductFormItem(optimizationItemToProduct(sourceItem))}
                    className="flex h-5 w-5 flex-shrink-0 items-center justify-center rounded-md disabled:cursor-not-allowed disabled:opacity-40"
                  >
                    <span className="relative inline-flex h-4 w-4" aria-hidden="true">
                      <Cuboid className="absolute inset-0 h-full w-full" fill={colorStyle} stroke="none" />
                      <Cuboid className="absolute inset-0 h-full w-full" fill="none" color="#fff" />
                    </span>
                  </button>
                  <p className={`font-bold break-all ${isDark ? 'text-red-400' : 'text-red-700'}`}>
                    {unloadedLabels.get(item)}
                  </p>
                </div>
                <div className="flex items-center gap-1.5">
                  {sourceItem?.requires_pallet && (
                    <span title="Palettisation automatique" className="flex-shrink-0">
                      <Table aria-label="Palettisation automatique" className="w-3.5 h-3.5 text-emerald-500" />
                    </span>
                  )}
                  <span className={`shrink-0 text-[10px] px-2 py-0.5 rounded-full font-bold ${isDark
                    ? 'bg-orange-900/40 text-orange-300 border border-orange-700'
                    : 'bg-orange-100 text-orange-800 border border-orange-300'
                    }`}>
                    {item.quantity ?? 1} colis restant{(item.quantity ?? 1) > 1 ? 's' : ''}
                  </span>
                </div>
              </div>
              {item.description && (
                <p className={`text-[10px] mt-0.5 ${isDark ? 'text-slate-400' : 'text-slate-600 font-medium'}`}>
                  {sourceItem ? `Ligne de cargaison : ${itemLabels.get(sourceItem.id)}` : item.description}
                </p>
              )}
              {(item.reason || item.dominant_rejection_reason || item.rejection_summary?.dominant_reason) && (
                <p className={`text-[10px] mt-1.5 font-semibold ${isDark ? 'text-amber-300' : 'text-amber-800'}`}>
                  {formatReason(item.reason, item.dominant_rejection_reason || item.rejection_summary?.dominant_reason)}
                </p>
              )}
              {reasonDetail(item) && (
                <p className={`text-[10px] mt-0.5 italic ${isDark ? 'text-slate-400' : 'text-slate-600'}`}>
                  {reasonDetail(item)}
                </p>
              )}
              {diagnosticDetail(item, run.input?.measurement_system ?? 'metric') && (
                <p className={`text-[11px] mt-1 leading-5 font-mono break-words ${isDark ? 'text-slate-400' : 'text-slate-600'}`}>
                  {diagnosticDetail(item, run.input?.measurement_system ?? 'metric')}
                </p>
              )}
              <div className={`text-[10px] mt-1.5 flex gap-3 font-medium ${isDark ? 'text-slate-400' : 'text-slate-700'}`}>
                {item.length != null && <span><Dimensions length={item.length} width={item.width} height={item.height} /></span>}
                {item.weight != null && <span><Measure value={item.weight} from="kg" quantity="mass" />{item.quantity && item.quantity > 1 ? ` (×${item.quantity})` : ''}</span>}
              </div>
            </div>
          )
        }) : (
          <div className={`py-8 text-center text-xs font-semibold ${isDark ? 'text-emerald-400' : 'text-emerald-700'}`}>
            {filteredUnloadedItems.length === 0 && unloadedItems.length > 0 ? 'Aucun colis non chargé trouvé' : 'Tous les articles ont été chargés avec succès'}
          </div>
        )}
      </div>
    </div>
  )

  // ── Tab 2: CG Content Helpers ──
  const cgContractReason = outputSummaryUnavailable
    ? 'Projection du résultat indisponible : le centre de gravité ne peut pas être certifié.'
    : outputSummary?.center_of_gravity?.reason ?? 'Centre de gravité non disponible dans le contrat de résultat.'
  const cgDeviationContent = () => cg ? (() => {
    const realCoords = [
      { id: 'X', name: 'Longitudinal', val: cg.x, c: '#3b82f6' },
      { id: 'Y', name: 'Transversal', val: cg.y, c: '#8b5cf6' },
      { id: 'Z', name: 'Vertical', val: cg.z, c: '#06b6d4' },
    ]
    const targetSource = outputSummary?.center_of_gravity.target_origin ?? targetCg?.source
      ?? run.result?.targetCenterOfGravity?.source ?? run.result?.target_center_of_gravity?.source
    const targetSourceLabel = ({ user_override: 'Cible définie par l’utilisateur', container_maritime: 'Centre géométrique du conteneur', default_center: 'Centre géométrique par défaut', semitrailer_physics: 'Cible calculée entre sellette et essieux', tractor_semitrailer_combined: 'Cible calculée pour l’ensemble tracteur et semi-remorque' } as Record<string, string>)[targetSource ?? ''] ?? 'Origine de la cible non renseignée'
    const targetCoords = targetCg ? [
      { id: 'X', name: 'Longitudinal', val: targetCg.x },
      { id: 'Y', name: 'Transversal', val: targetCg.y },
      { id: 'Z', name: 'Vertical', val: targetCg.z },
    ] : null

    const loadSpaceLength = Number(run.input.loadSpace?.length) || 13600
    const loadSpaceWidth = Number(run.input.loadSpace?.width) || 2500
    const aspectRatio = loadSpaceLength / loadSpaceWidth
    const svgWidth = 280
    const padding = 28
    const mapWidth = svgWidth - (padding * 2)
    // Apply the vehicle ratio to the floor itself, before adding SVG margins.
    // Subtracting margins from svgWidth / aspectRatio can invert the Y axis.
    const mapHeight = mapWidth / aspectRatio
    const svgHeight = mapHeight + (padding * 2)

    const advancedAnalysis = run.result?.advancedAxleAnalysis
    const isCombined = advancedAnalysis?.isCombined || false
    const combination = advancedAnalysis?.combination

    return (
      <div className="space-y-4">
        <CGDiagnostics analysis={run.result?.advancedAxleAnalysis} />

        {/* Balance Warning */}
        {balanceWarning && (
          <div className={`rounded-xl border p-3 text-xs ${isDark ? 'bg-amber-500/10 border-amber-500/20 text-amber-300' : 'bg-amber-50 border-amber-300 text-amber-900'}`}>
            <div className="flex items-center gap-2 font-bold mb-1">
              <ScaleIcon className={`w-4 h-4 ${isDark ? 'text-amber-400' : 'text-amber-600'}`} />
              <span>Avertissement de Balance</span>
            </div>
            <p className="whitespace-pre-wrap">{formatWarningMessage(balanceWarning, run.input?.measurement_system ?? 'metric')}</p>
          </div>
        )}

        {(loadPathWarnings.length > 0 || floorReactionWarnings.length > 0) && (
          <div className={`rounded-xl border p-3 text-xs ${isDark ? 'bg-amber-500/10 border-amber-500/20 text-amber-300' : 'bg-amber-50 border-amber-300 text-amber-900'}`}>
            <div className="flex items-center gap-2 font-bold mb-1">
              <ExclamationTriangleIcon className={`w-4 h-4 ${isDark ? 'text-amber-400' : 'text-amber-600'}`} />
              <span>Contrôle physique complémentaire requis</span>
            </div>
            <p>
              {loadPathWarnings.length + floorReactionWarnings.length} contrôle{loadPathWarnings.length + floorReactionWarnings.length > 1 ? 's' : ''} indique{loadPathWarnings.length + floorReactionWarnings.length > 1 ? 'nt' : ''} une charge transmise de façon excentrée.
              L’équilibre local n’est pas démontré par ce modèle. Vérifier les piles concernées avant exécution ; le statut global couvre uniquement les contrôles réalisés.
            </p>
            <div className="mt-2 grid grid-cols-2 gap-2">
              <div className={`rounded-lg p-2 ${isDark ? 'bg-black/20' : 'bg-white/70'}`}>
                <p className="text-[9px] uppercase font-bold opacity-70">Piles</p>
                <p className="font-black">{loadPathWarnings.length}</p>
              </div>
              <div className={`rounded-lg p-2 ${isDark ? 'bg-black/20' : 'bg-white/70'}`}>
                <p className="text-[9px] uppercase font-bold opacity-70">Réactions sol</p>
                <p className="font-black">{floorReactionWarnings.length}</p>
              </div>
            </div>
          </div>
        )}

        {/* Combined Tractor-Trailer Card */}
        {isCombined && combination && (
          <div className={`rounded-xl border p-3 text-xs ${isDark ? 'bg-blue-500/10 border-blue-500/20' : 'bg-blue-50 border-blue-200'}`}>
            <div className={`flex items-center gap-2 font-bold mb-2 ${isDark ? 'text-blue-300' : 'text-blue-800'}`}>
              <TruckIcon className={`w-4 h-4 ${isDark ? 'text-blue-400' : 'text-blue-600'}`} />
              <span>Configuration Combinée</span>
            </div>
            <div className="grid grid-cols-3 gap-2 text-center">
              <div className={`p-1.5 rounded-lg ${isDark ? 'bg-black/20' : 'bg-slate-100 border border-slate-200'}`}>
                <p className={`text-[9px] ${isDark ? 'text-slate-400' : 'text-slate-600'}`}>Tracteur</p>
                <p className={`font-bold ${isDark ? 'text-white' : 'text-slate-900'}`}><Measure value={combination.tractorTotalWeight} from="kg" quantity="mass" /></p>
              </div>
              <div className={`p-1.5 rounded-lg ${isDark ? 'bg-black/20' : 'bg-slate-100 border border-slate-200'}`}>
                <p className={`text-[9px] ${isDark ? 'text-slate-400' : 'text-slate-600'}`}>Remorque</p>
                <p className={`font-bold ${isDark ? 'text-white' : 'text-slate-900'}`}><Measure value={combination.trailerTotalWeight} from="kg" quantity="mass" /></p>
              </div>
              <div className={`p-1.5 rounded-lg ${isDark ? 'bg-black/20' : 'bg-slate-100 border border-slate-200'}`}>
                <p className={`text-[9px] ${isDark ? 'text-slate-400' : 'text-slate-600'}`}>Total</p>
                <p className={`font-bold ${isDark ? 'text-white' : 'text-slate-900'}`}><Measure value={combination.totalCombinationWeight} from="kg" quantity="mass" /></p>
              </div>
            </div>
          </div>
        )}

        {/* 2D CG Plan & Table */}
        <div className={`rounded-xl border p-3 space-y-3 ${isDark ? 'bg-white/[0.03] border-white/[0.08]' : 'bg-slate-50 border-slate-200'}`}>
          {/* Header + légende pills inline */}
          <div className="flex items-center justify-between gap-2">
            <div className="flex items-center gap-2">
              <XCircleIcon className="w-4 h-4 text-blue-500" />
              <span className={`text-xs font-bold ${isDark ? 'text-slate-200' : 'text-slate-800'}`}>Positionnement 2D (Plancher)</span>
            </div>
            {/* Pills de toggle 3D — remplacent la section «Affichage 3D» supprimée */}
            <div className="flex items-center gap-1.5">
              <button
                type="button"
                onClick={() => onShowRealCGChange?.(!showRealCG)}
                title={showRealCG ? 'Masquer le CG réel dans la vue 3D' : 'Afficher le CG réel dans la vue 3D'}
                className={`flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-semibold border transition-all duration-150 ${showRealCG
                  ? 'bg-blue-500/20 border-blue-500/50 text-blue-400'
                  : isDark ? 'bg-white/[0.05] border-white/[0.12] text-slate-500 hover:text-slate-300' : 'bg-slate-100 border-slate-300 text-slate-400 hover:text-slate-600'
                  }`}
              >
                <span className={`w-1.5 h-1.5 rounded-full ${showRealCG ? 'bg-blue-400' : isDark ? 'bg-slate-600' : 'bg-slate-300'}`} />
                Calculé
              </button>
              {targetCg && <p className="text-[10px]" title={targetSource ?? undefined}>{targetSourceLabel}</p>}
              {targetCg && (
                <button
                  type="button"
                  onClick={() => onShowTargetCGChange?.(!showTargetCG)}
                  title={showTargetCG ? 'Masquer le CG cible dans la vue 3D' : 'Afficher le CG cible dans la vue 3D'}
                  className={`flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-semibold border transition-all duration-150 ${showTargetCG
                    ? 'bg-emerald-500/20 border-emerald-500/50 text-emerald-400'
                    : isDark ? 'bg-white/[0.05] border-white/[0.12] text-slate-500 hover:text-slate-300' : 'bg-slate-100 border-slate-300 text-slate-400 hover:text-slate-600'
                    }`}
                >
                  <span className={`w-1.5 h-1.5 rounded-full ${showTargetCG ? 'bg-emerald-400' : isDark ? 'bg-slate-600' : 'bg-slate-300'}`} />
                  Cible
                </button>
              )}
            </div>
          </div>

          <svg viewBox={`0 0 ${svgWidth} ${svgHeight + 20}`} className={`w-full rounded-lg border ${isDark ? 'bg-black/40 border-white/[0.08]' : 'bg-slate-200/60 border-slate-300'}`}>
            <defs>
              <marker id="cgArrow" viewBox="0 0 8 8" refX="4" refY="4" markerWidth="5" markerHeight="5" orient="auto-start-reverse">
                <path d="M0,0 L8,4 L0,8 z" fill="#f59e0b" />
              </marker>
            </defs>
            <rect x={padding} y={padding} width={mapWidth} height={mapHeight} fill="none" stroke="#475569" strokeWidth="1" rx="2" />
            <line x1={svgWidth / 2} y1={padding} x2={svgWidth / 2} y2={padding + mapHeight} stroke="#94a3b8" strokeWidth="1" strokeDasharray="3" />
            <line x1={padding} y1={padding + mapHeight / 2} x2={padding + mapWidth} y2={padding + mapHeight / 2} stroke="#94a3b8" strokeWidth="1" strokeDasharray="3" />

            {/* Flèche réel → cible */}
            {targetCg && (
              <line
                x1={padding + (cg.x / loadSpaceLength) * mapWidth}
                y1={padding + (1 - cg.y / loadSpaceWidth) * mapHeight}
                x2={padding + (targetCg.x / loadSpaceLength) * mapWidth}
                y2={padding + (1 - targetCg.y / loadSpaceWidth) * mapHeight}
                stroke="#f59e0b"
                strokeWidth="1.5"
                strokeDasharray="3 2"
                markerEnd="url(#cgArrow)"
              />
            )}

            {/* Point CG cible — cliquable, toggle showTargetCG */}
            {targetCg && (
              <g
                onClick={() => onShowTargetCGChange?.(!showTargetCG)}
                style={{ cursor: 'pointer' }}
              >
                <title>{showTargetCG ? 'Masquer le CG cible dans la vue 3D' : 'Afficher le CG cible dans la vue 3D'}</title>
                {/* Halo d'état actif */}
                {showTargetCG && (
                  <circle
                    cx={padding + (targetCg.x / loadSpaceLength) * mapWidth}
                    cy={padding + (1 - targetCg.y / loadSpaceWidth) * mapHeight}
                    r="9"
                    fill="none"
                    stroke="#10b981"
                    strokeWidth="1.5"
                    strokeDasharray="none"
                    opacity="0.5"
                  />
                )}
                {/* Zone de clic élargie (invisible) */}
                <circle
                  cx={padding + (targetCg.x / loadSpaceLength) * mapWidth}
                  cy={padding + (1 - targetCg.y / loadSpaceWidth) * mapHeight}
                  r="12"
                  fill="transparent"
                />
                {/* Point visible */}
                <circle
                  cx={padding + (targetCg.x / loadSpaceLength) * mapWidth}
                  cy={padding + (1 - targetCg.y / loadSpaceWidth) * mapHeight}
                  r="5"
                  fill={showTargetCG ? '#10b981' : '#334155'}
                  stroke="#10b981"
                  strokeWidth="1.5"
                />
              </g>
            )}

            {/* Point CG réel — cliquable, toggle showRealCG */}
            <g
              onClick={() => onShowRealCGChange?.(!showRealCG)}
              style={{ cursor: 'pointer' }}
            >
              <title>{showRealCG ? 'Masquer le CG réel dans la vue 3D' : 'Afficher le CG réel dans la vue 3D'}</title>
              {/* Halo d'état actif */}
              {showRealCG && (
                <circle
                  cx={padding + (cg.x / loadSpaceLength) * mapWidth}
                  cy={padding + (1 - cg.y / loadSpaceWidth) * mapHeight}
                  r="9"
                  fill="none"
                  stroke="#3b82f6"
                  strokeWidth="1.5"
                  opacity="0.5"
                />
              )}
              {/* Zone de clic élargie (invisible) */}
              <circle
                cx={padding + (cg.x / loadSpaceLength) * mapWidth}
                cy={padding + (1 - cg.y / loadSpaceWidth) * mapHeight}
                r="12"
                fill="transparent"
              />
              {/* Point visible */}
              <circle
                cx={padding + (cg.x / loadSpaceLength) * mapWidth}
                cy={padding + (1 - cg.y / loadSpaceWidth) * mapHeight}
                r="5"
                fill={showRealCG ? '#3b82f6' : '#334155'}
                stroke="#3b82f6"
                strokeWidth="1.5"
              />
            </g>
          </svg>

          <p className="text-xs text-slate-500 mb-2">Centre calculé à partir des masses et des centres de gravité renseignés, sinon des centres géométriques. La cible Z reprend la hauteur calculée ; elle ne constitue pas une hauteur recommandée.</p>
          {/* Table */}
          <table className={`w-full text-xs ${isDark ? 'text-slate-300' : 'text-slate-800'}`}>
            <thead>
              <tr className={`border-b text-[9px] uppercase ${isDark ? 'border-white/[0.08] text-slate-400' : 'border-slate-200 text-slate-600'}`}>
                <th className="text-left py-1 w-14">Axe</th>
                <th className="text-right py-1 text-blue-500">Calculé</th>
                {targetCoords && <th className="text-right py-1 text-emerald-500">Cible</th>}
                {targetCoords && <th className={`text-right py-1 ${isDark ? 'text-slate-300' : 'text-slate-700'}`}>Écart</th>}
              </tr>
            </thead>
            <tbody>
              {realCoords.map((r, i) => {
                const t = targetCoords?.[i]
                const diff = t ? r.val - t.val : null
                const deviation = advancedAnalysis?.targetCG?.deviation
                const ratio = r.id === 'X' ? deviation?.deviationX ?? (diff !== null ? Math.abs(diff) / loadSpaceLength : null)
                  : r.id === 'Y' ? deviation?.deviationY ?? (diff !== null ? Math.abs(diff) / loadSpaceWidth : null) : null

                // Couleur du badge d'écart selon le ratio
                const ratioPct = (ratio != null && Number.isFinite(ratio)) ? ratio * 100 : null
                const ecartBadge = ratioPct == null
                  ? { cls: isDark ? 'bg-slate-700/60 text-slate-300' : 'bg-slate-100 text-slate-600' }
                  : ratioPct <= 5
                    ? { cls: isDark ? 'bg-emerald-500/20 text-emerald-300' : 'bg-emerald-50 text-emerald-700' }
                    : ratioPct <= 15
                      ? { cls: isDark ? 'bg-amber-500/20 text-amber-300' : 'bg-amber-50 text-amber-700' }
                      : { cls: isDark ? 'bg-red-500/20 text-red-300' : 'bg-red-50 text-red-700' }

                return (
                  <tr key={r.id} className={`border-b ${isDark ? 'border-white/[0.04]' : 'border-slate-100'}`}>
                    {/* Axe compact : lettre grande + nom sous-titre */}
                    <td className="py-1.5 w-14">
                      <span className={`font-black text-sm leading-none ${isDark ? 'text-white' : 'text-slate-900'}`}>{r.id}</span>
                      <span className={`block text-[9px] leading-none mt-0.5 ${isDark ? 'text-slate-500' : 'text-slate-400'}`}>{r.name}</span>
                    </td>
                    <td className="py-1.5 text-right font-bold text-blue-500 tabular-nums"><Measure value={r.val} from="mm" quantity="vehicleLength" /></td>
                    {t && <td className="py-1.5 text-right font-bold text-emerald-500 tabular-nums"><Measure value={t.val} from="mm" quantity="vehicleLength" /></td>}
                    {diff !== null && (
                      <td className="py-1.5 text-right">
                        <span
                          className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded font-bold tabular-nums text-[10px] ${ecartBadge.cls}`}
                          title={ratioPct != null ? `Écart absolu rapporté à la ${r.id === 'X' ? 'longueur' : 'largeur'} de l'espace : ${ratioPct.toFixed(1)}%` : undefined}
                        >
                          <span>{diff > 0 ? '+' : ''}<Measure value={diff} from="mm" quantity="vehicleLength" /></span>
                          {ratioPct != null && <span className="opacity-70 font-normal">{ratioPct.toFixed(1)}%</span>}
                        </span>
                      </td>
                    )}
                  </tr>
                )
              })}
            </tbody>
          </table>
          <CGConclusion deviation={outputSummary?.physical_analysis?.center_of_gravity.deviation} analysis={advancedAnalysis} isDark={isDark} />
          {targetCoords && <p className={`text-[9px] mt-1 italic ${isDark ? 'text-slate-500' : 'text-slate-400'}`}>Z cible reprend la hauteur réelle comme référence ; l'écart vertical n'est pas un contrôle indépendant.</p>}
        </div>
      </div>
    )
  })() : (
    <div role="alert" className={`rounded-xl border p-3 text-xs ${isDark ? 'bg-amber-500/10 border-amber-500/20 text-amber-200' : 'bg-amber-50 border-amber-300 text-amber-900'}`}>
      <p className="font-bold">Centre de gravité non vérifié</p>
      <p className="mt-1">{cgContractReason}</p>
    </div>
  )

  const lateralBalanceContent = () => <LateralBalanceSection analysis={run.result?.advancedAxleAnalysis} centerOfGravity={cg} loadSpace={run.result?.load_space} isDark={isDark} />

  // ── Tab 5: Stability Content ──
  const getRiskColor = (level: string) => {
    switch (level) {
      case 'critical': return 'text-red-400 bg-red-500/20 border-red-500/30'
      case 'high': return 'text-orange-400 bg-orange-500/20 border-orange-500/30'
      case 'moderate': return 'text-amber-400 bg-amber-500/20 border-amber-500/30'
      case 'low': return 'text-emerald-400 bg-emerald-500/20 border-emerald-500/30'
      default: return 'text-slate-400 bg-slate-500/20 border-slate-500/30'
    }
  }

  const stabilityIndicatorsContent = () => ds ? (
    <div className="space-y-3 text-xs">
      {/* Overall Risk Header Card */}
      <div className={`p-3 rounded-xl border ${isDark ? 'bg-white/[0.03] border-white/[0.08]' : 'bg-slate-50 border-slate-200'}`}>
        <div className="flex items-center justify-between gap-2">
          <div>
            <div className="flex items-center gap-1.5">
              <p className={`text-[10px] uppercase font-bold ${isDark ? 'text-slate-400' : 'text-slate-600'}`}>Niveau de Risque Global</p>
              <StabilityIndicatorHelp indicator="global" isDark={isDark} />
            </div>
            <p className={`text-xs font-bold mt-0.5 ${isDark ? 'text-slate-100' : 'text-slate-900'}`}>
              {ds.overallRiskLevel === 'critical' ? 'Risque Critique de Basculement' :
                ds.overallRiskLevel === 'high' ? 'Risque Élevé en Virage' :
                  ds.overallRiskLevel === 'moderate' ? 'Indicateur modéré' :
                    ds.overallRiskLevel === 'low' ? 'Indicateur faible (modèle interne)' : 'Risque Non Déterminé'}
            </p>
          </div>
          <span className={`px-2 py-0.5 rounded-full text-[10px] font-bold border uppercase tracking-wider ${getRiskColor(ds.overallRiskLevel)}`}>
            {ds.overallRiskLevel === 'critical' ? 'Risque CRITIQUE' :
              ds.overallRiskLevel === 'high' ? 'Risque ÉLEVÉ' :
                ds.overallRiskLevel === 'moderate' ? 'Risque MODÉRÉ' :
                  ds.overallRiskLevel === 'low' ? 'Risque FAIBLE' : 'Risque INCONNU'}
          </span>
        </div>
      </div>

      {(loadPathWarnings.length > 0 || floorReactionWarnings.length > 0) && (
        <div className={`p-3 rounded-xl border ${isDark ? 'bg-amber-500/10 border-amber-500/20' : 'bg-amber-50 border-amber-200'}`}>
          <div className="flex justify-between items-center">
            <div className="flex items-center gap-1.5">
              <span className={`font-bold ${isDark ? 'text-amber-300' : 'text-amber-900'}`}>Stabilité locale des piles</span>
              <StabilityIndicatorHelp indicator="local" isDark={isDark} />
            </div>
            <span className={`font-bold text-xs ${isDark ? 'text-amber-200' : 'text-amber-800'}`}>
              {loadPathWarnings.length + floorReactionWarnings.length} cas
            </span>
          </div>
          <p className={`mt-1 text-[10px] ${isDark ? 'text-amber-200/80' : 'text-amber-800'}`}>
            Le centre de gravité global peut être correct tout en gardant des transmissions locales excentrées. Vérifier ces cas dans l’onglet Pression, puis centrer les colis empilés ou renforcer le support si nécessaire.
          </p>
        </div>
      )}

      {/* Rollover Index */}
      <div className={`p-3 rounded-xl border space-y-1.5 ${isDark ? 'bg-white/[0.03] border-white/[0.08]' : 'bg-slate-50 border-slate-200'}`}>
        <div className="flex justify-between items-center">
          <div className="flex items-center gap-1.5">
            <span className={`font-bold ${isDark ? 'text-slate-200' : 'text-slate-800'}`}>Indice de renversement</span>
            <StabilityIndicatorHelp indicator="rollover" isDark={isDark} />
          </div>
          <span className={`font-bold text-xs font-mono ${isDark ? 'text-white' : 'text-slate-900'}`}>
            {ds.rolloverIndex != null ? ds.rolloverIndex.toFixed(2) : '—'}
          </span>
        </div>
        {ds.rolloverIndex != null ? (
          <>
            <div className={`relative h-2 rounded-full overflow-hidden ${isDark ? 'bg-slate-800' : 'bg-slate-200'}`}>
              <div className="absolute h-full transition-all duration-300 rounded-full" style={{ width: `${Math.min(100, (ds.rolloverIndex / 1.0) * 100)}%`, background: ds.rolloverIndex <= 0.5 ? '#22c55e' : ds.rolloverIndex <= 0.7 ? '#eab308' : ds.rolloverIndex <= 0.8 ? '#f97316' : '#ef4444' }} />
            </div>
            <div className={`flex justify-between text-[9px] font-mono ${isDark ? 'text-slate-500' : 'text-slate-500'}`}>
              <span>0.0</span><span>0.5</span><span>0.7</span><span>0.8</span><span>1.0</span>
            </div>
            <div className="flex items-center gap-2 pt-0.5">
              <span className={`text-[9px] font-bold px-1.5 py-0.5 rounded ${ds.rolloverIndex <= 0.5 ? 'bg-emerald-500/20 text-emerald-600' : ds.rolloverIndex <= 0.7 ? 'bg-amber-500/20 text-amber-600' : ds.rolloverIndex <= 0.8 ? 'bg-orange-500/20 text-orange-600' : 'bg-red-500/20 text-red-600'}`}>
                {ds.rolloverIndex <= 0.5 ? 'Risque FAIBLE' : ds.rolloverIndex <= 0.7 ? 'Risque MODÉRÉ' : ds.rolloverIndex <= 0.8 ? 'Risque ÉLEVÉ' : 'Risque CRITIQUE'}
              </span>
              <span className={`text-[10px] truncate ${isDark ? 'text-slate-400' : 'text-slate-600'}`}>
                {ds.rolloverIndex <= 0.5 ? 'Renversement peu probable' : ds.rolloverIndex <= 0.7 ? 'Vigilance recommandée' : ds.rolloverIndex <= 0.8 ? 'Risque de renversement' : 'Risque critique de renversement'}
              </span>
            </div>
          </>
        ) : (
          <p className={`text-[10px] ${isDark ? 'text-slate-500' : 'text-slate-500'}`}>Indice non calculé</p>
        )}
      </div>

      {/* Yaw Risk Factor */}
      <div className={`p-3 rounded-xl border space-y-1.5 ${isDark ? 'bg-white/[0.03] border-white/[0.08]' : 'bg-slate-50 border-slate-200'}`}>
        <div className="flex justify-between items-center">
          <div className="flex items-center gap-1.5">
            <span className={`font-bold ${isDark ? 'text-slate-200' : 'text-slate-800'}`}>Facteur de Lacet (Yaw)</span>
            <StabilityIndicatorHelp indicator="yaw" isDark={isDark} />
          </div>
          <span className={`font-bold text-xs font-mono ${isDark ? 'text-white' : 'text-slate-900'}`}>
            {ds.yawRiskFactor != null ? `${(ds.yawRiskFactor * 100).toFixed(0)}%` : '—'}
          </span>
        </div>
        {ds.yawRiskFactor != null ? (
          <>
            <div className={`relative h-2 rounded-full overflow-hidden ${isDark ? 'bg-slate-800' : 'bg-slate-200'}`}>
              <div className="absolute h-full transition-all duration-300 rounded-full" style={{ width: `${Math.min(100, ds.yawRiskFactor * 100)}%`, background: ds.yawRiskFactor <= 0.3 ? '#22c55e' : ds.yawRiskFactor <= 0.5 ? '#eab308' : ds.yawRiskFactor <= 0.7 ? '#f97316' : '#ef4444' }} />
            </div>
            <div className={`flex justify-between text-[9px] font-mono ${isDark ? 'text-slate-500' : 'text-slate-500'}`}>
              <span>0%</span><span>30%</span><span>50%</span><span>70%</span><span>100%</span>
            </div>
            <div className="flex items-center gap-2 pt-0.5">
              <span className={`text-[9px] font-bold px-1.5 py-0.5 rounded ${ds.yawRiskFactor <= 0.3 ? 'bg-emerald-500/20 text-emerald-600' : ds.yawRiskFactor <= 0.5 ? 'bg-amber-500/20 text-amber-600' : ds.yawRiskFactor <= 0.7 ? 'bg-orange-500/20 text-orange-600' : 'bg-red-500/20 text-red-600'}`}>
                {ds.yawRiskFactor <= 0.3 ? 'Risque FAIBLE' : ds.yawRiskFactor <= 0.5 ? 'Risque MODÉRÉ' : ds.yawRiskFactor <= 0.7 ? 'Risque ÉLEVÉ' : 'Risque CRITIQUE'}
              </span>
              <span className={`text-[10px] truncate ${isDark ? 'text-slate-400' : 'text-slate-600'}`}>
                {ds.yawRiskFactor <= 0.3 ? 'Lacet peu probable' : ds.yawRiskFactor <= 0.5 ? 'Déséquilibre latéral modéré' : ds.yawRiskFactor <= 0.7 ? 'Déséquilibre latéral élevé' : 'Lacet critique'}
              </span>
            </div>
          </>
        ) : (
          <p className={`text-[10px] ${isDark ? 'text-slate-500' : 'text-slate-500'}`}>Indice non calculé</p>
        )}
      </div>

      {/* Roll Stability Ratio */}
      <div className={`p-3 rounded-xl border space-y-1.5 ${isDark ? 'bg-white/[0.03] border-white/[0.08]' : 'bg-slate-50 border-slate-200'}`}>
        <div className="flex justify-between items-center">
          <div className="flex items-center gap-1.5">
            <span className={`font-bold ${isDark ? 'text-slate-200' : 'text-slate-800'}`}>Roulis (Roll Ratio)</span>
            <StabilityIndicatorHelp indicator="roll" isDark={isDark} />
          </div>
          <span className={`font-bold text-xs font-mono ${isDark ? 'text-white' : 'text-slate-900'}`}>
            {ds.rollStabilityRatio != null ? ds.rollStabilityRatio.toFixed(2) : '—'}
          </span>
        </div>
        {ds.rollStabilityRatio != null ? (
          <>
            <div className={`relative h-2 rounded-full overflow-hidden ${isDark ? 'bg-slate-800' : 'bg-slate-200'}`}>
              <div className="absolute h-full transition-all duration-300 rounded-full" style={{ width: `${Math.min(100, ds.rollStabilityRatio * 100)}%`, background: ds.rollStabilityRatio <= 0.4 ? '#22c55e' : ds.rollStabilityRatio <= 0.6 ? '#eab308' : ds.rollStabilityRatio <= 0.8 ? '#f97316' : '#ef4444' }} />
            </div>
            <div className={`flex justify-between text-[9px] font-mono ${isDark ? 'text-slate-500' : 'text-slate-500'}`}>
              <span>0.0</span><span>0.4</span><span>0.6</span><span>0.8</span><span>1.0</span>
            </div>
            <div className="flex items-center gap-2 pt-0.5">
              <span className={`text-[9px] font-bold px-1.5 py-0.5 rounded ${ds.rollStabilityRatio <= 0.4 ? 'bg-emerald-500/20 text-emerald-600' : ds.rollStabilityRatio <= 0.6 ? 'bg-amber-500/20 text-amber-600' : ds.rollStabilityRatio <= 0.8 ? 'bg-orange-500/20 text-orange-600' : 'bg-red-500/20 text-red-600'}`}>
                {ds.rollStabilityRatio <= 0.4 ? 'Risque FAIBLE' : ds.rollStabilityRatio <= 0.6 ? 'Risque MODÉRÉ' : ds.rollStabilityRatio <= 0.8 ? 'Risque ÉLEVÉ' : 'Risque CRITIQUE'}
              </span>
              <span className={`text-[10px] truncate ${isDark ? 'text-slate-400' : 'text-slate-600'}`}>
                {ds.rollStabilityRatio <= 0.4 ? 'Inclinaison minime' : ds.rollStabilityRatio <= 0.6 ? 'Inclinaison latérale modérée' : ds.rollStabilityRatio <= 0.8 ? 'Roulis élevé' : 'Roulis critique'}
              </span>
            </div>
          </>
        ) : (
          <p className={`text-[10px] ${isDark ? 'text-slate-500' : 'text-slate-500'}`}>Indice non calculé</p>
        )}
      </div>

      {/* Disclaimer stabilité */}
      <div className={`p-2 rounded-lg text-[10px] italic border ${isDark ? 'bg-slate-800/40 border-white/[0.06] text-slate-500' : 'bg-slate-100 border-slate-200 text-slate-500'}`}>
        Ces indicateurs proviennent du modèle de calcul interne. L'arrimage et la stabilité routière complète (freinage, lacet réel) doivent être vérifiés séparément.
      </div>
    </div>
  ) : (
    <div className={`text-center py-6 text-xs ${isDark ? 'text-slate-400' : 'text-slate-600'}`}>
      <p className="font-semibold">Analyse non disponible</p>
      <p className="text-[10px] mt-1 opacity-70">Aucune donnée de stabilité dynamique n'a été fournie par le moteur pour cette exécution.</p>
    </div>
  )

  // ── Tab 6: Alerts Content ──
  const alertCargoContent = (alert: any) => {
    const ids = alertItemIds(alert)
    if (!ids.length) return null
    const cargo = resolveAlertCargo(alert, viewerRun)
    const locate = (itemIds: string[]) => {
      onLocateItems?.(itemIds)
      setIsPanelOpen(false)
    }
    return (
      <div className="mt-2 space-y-2">
        <p className="font-semibold">Colis concernés ({cargo.length || ids.length})</p>
        <div className="flex max-h-32 flex-wrap gap-1 overflow-y-auto">
          {cargo.length ? cargo.map(item => (
            <button key={item.id} type="button" disabled={!onLocateItems}
              onClick={() => locate([item.id])}
              title={`Localiser ${item.label.replace(/\n/g, ' — ')}`}
              className="whitespace-pre-line rounded border border-current/20 px-2 py-1 text-left hover:bg-blue-500/10 focus-visible:ring-2 focus-visible:ring-blue-500">
              {item.label}
            </button>
          )) : ids.map(id => <span key={id}>{cargoDisplayId(id)}</span>)}
        </div>
        {cargo.length > 1 && onLocateItems && (
          <button type="button" onClick={() => locate(cargo.map(item => item.id))}
            className="rounded-lg bg-blue-600 px-3 py-2 font-semibold text-white hover:bg-blue-500 focus-visible:ring-2 focus-visible:ring-blue-400">
            Localiser tous les colis ({cargo.length})
          </button>
        )}
        {cargo.length === 0 && <p>Localisation indisponible : ces colis ne sont pas présents dans le plan chargé.</p>}
      </div>
    )
  }

  const warningsListContent = () => (
    <div className="space-y-2">
      {warnings.length === 0 ? (
        <div className="text-center py-8">
          <CheckCircleIcon className="w-8 h-8 text-emerald-500 mx-auto mb-2" />
          <p className={`text-xs font-semibold text-emerald-600`}>Aucun avertissement</p>
        </div>
      ) : (
        groupWarningsForDisplay(warnings).map((w, idx) => {
          const Icon = getWarningIcon(w)
          return (
            <div key={idx} className={`p-3 rounded-xl border text-xs flex items-start gap-2.5 ${isDark ? 'bg-amber-500/10 border-amber-500/20 text-amber-300' : 'bg-amber-50 border-amber-200 text-amber-800'}`}>
              <Icon className={`w-4 h-4 flex-shrink-0 mt-0.5 ${isDark ? 'text-amber-400' : 'text-amber-600'}`} />
              <div className="min-w-0 flex-1">
                <p className="mb-1 text-[10px] font-bold uppercase">{alertSeverity(w.severity)}</p>
                <p className="whitespace-pre-wrap">{formatWarningMessage(w, run.input?.measurement_system ?? 'metric')}</p>
                {(w.measurement || w.details) && <AlertMeasurement alert={w} system={run.input.measurement_system ?? 'metric'} />}
                {alertCargoContent(w)}
              </div>
            </div>
          )
        })
      )}
    </div>
  )


  // Operational summary only; full configuration and timings remain in JSON.
  const diagnosticsContent = () => {
    const diagnostics = outputSummary?.optimization_diagnostics
    const metrics = (run.result as any)?.metrics ?? {}
    const termination = diagnostics?.termination_reason ?? metrics.termination_reason ?? run.status
    const duration = diagnostics?.execution_time_ms ?? metrics.execution_time_ms ?? run.result?.execution_time_ms
    const budgetReached = diagnostics?.candidate_budget_exhausted ?? metrics.candidate_budget_exhausted
    const temperature = (run.result as any)?.temperature_compatibility ?? (run.result as any)?.temperatureCompatibility
    return <div className="space-y-3 text-xs leading-relaxed">
      <p className="font-semibold">Synthèse du calcul</p>
      <p>Colis chargés : {loadedCartonsCount ?? '—'} / {totalCartonsCount ?? '—'} ; non chargés : {outputSummary?.cargo.remaining ?? unloadedCount}.</p>
      <p>Fin de recherche : {diagnosticLabel('termination', termination)}</p>
      <p>Durée : {typeof duration === 'number' ? `${(duration / 1000).toLocaleString('fr-FR', { maximumFractionDigits: 1 })} s` : 'Non renseignée'}</p>
      {budgetReached === true && <p className="rounded border border-amber-500/30 p-2">Le budget de recherche a été atteint. Une recherche plus approfondie peut améliorer le chargement.</p>}
      {temperature && <p>Température : {temperature.incompatibleItems ?? '—'} colis incompatibles ; {temperature.limitedOverlapItems ?? '—'} plages partiellement compatibles. Voir les alertes.</p>}
      {outputSummary?.data_quality.status === 'attention' && <p className="rounded border border-amber-500/30 p-2">Certaines données historiques nécessitent une vérification. Le détail est disponible dans l’export JSON.</p>}
      <p className="text-slate-500">Les identifiants techniques, la configuration et les diagnostics détaillés sont disponibles dans Exporter → JSON.</p>
    </div>
  }

  // ── Build Tabs Configuration ─────────────────────────────────────────────
  const tabsConfig: TabItem[] = [
    {
      id: 'articles',
      label: 'Articles',
      icon: <ListBulletIcon className="w-4 h-4" />,
      indicatorTone: unloadedCount > 0 ? 'warning' : 'success',
      badge: (
        <span className="px-1.5 py-0.5 text-[9px] font-bold rounded-full bg-blue-500/20 text-blue-300 border border-blue-500/30">
          {loadedCartonsCount ?? '—'}/{totalCartonsCount}
        </span>
      ),
      subTabs: [
        { id: 'items', label: `Colis (${items.length})`, content: articlesItemsContent },
        { id: 'pallets', label: `Palettes (${validPalletGroups.length})`, content: articlesPalletsContent },
        { id: 'unloaded', label: `Non chargés (${unloadedCount} colis)`, content: unloadedItemsContent },
      ],
    },
    {
      id: 'axle_cg',
      label: 'Essieux',
      icon: <ShieldHalf className="w-4 h-4" />,
      indicatorTone: axleCompliance === true ? 'success' : axleCompliance === false ? 'danger' : 'neutral',
      badge: balanceWarning ? <span className="w-2 h-2 rounded-full bg-amber-400 animate-pulse" /> : undefined,
      subTabs: [
        {
          id: 'axle',
          label: 'Essieux 2D',
          content: () => <AxleOverlay2D run={run} outputSummary={outputSummary} embedded={true} />,
        },
        {
          id: 'recommendations', label: 'Recommandations', content: () => (
            <StabilityRecommendationsPanel
              run={run}
              embedded={true}
              isDark={isDark}
              onRecommendationClick={(rec) => {
                _setSelectedRecommendation?.(rec)
                _setSelectedItemIds?.([rec.item_reference])
              }}
            />
          )
        },
      ],
    },
    {
      id: 'cg',
      label: 'Centre de Gravité',
      icon: <Crosshair className="w-4 h-4" />,
      indicatorTone: cgAlert || outputSummaryUnavailable || outputSummary?.center_of_gravity?.status === 'unverified' ? 'warning' : 'success',
      badge: cgAlert || outputSummaryUnavailable || outputSummary?.center_of_gravity?.status === 'unverified'
        ? <span className="text-amber-500" aria-label="Alerte centre de gravité">⚠</span> : undefined,
      subTabs: [
        {
          id: 'cg_deviation',
          label: 'Écart CG',
          content: cgDeviationContent,
        },
        {
          id: 'lateral_balance',
          label: 'Équilibre latéral',
          content: lateralBalanceContent,
        },
      ],
    },
    {
      id: 'pressure',
      label: 'Pression Sol',
      icon: <CircleGauge className="w-4 h-4" />,
      indicatorTone: pressureIsOkay === undefined ? 'neutral'
        : pressureIsOkay ? 'success' : pressurePolicy === 'simulation' ? 'caution' : 'danger',
      content: () => (
        <div className="space-y-3">
          {_selectedPressureZone && (
            <div className="px-3 py-2 bg-blue-500/10 border border-blue-500/20 rounded-xl flex items-center justify-between text-xs">
              <span className="text-blue-400 font-medium flex items-center gap-1.5">
                <FunnelIcon className="w-3.5 h-3.5 text-blue-400" />
                Filtre zone pression : <strong className="text-blue-300 font-bold">{_selectedPressureZone.itemIds.length} colis concerné(s)</strong>
              </span>
              <button
                type="button"
                onClick={() => _setSelectedPressureZone?.(null)}
                className="text-xs font-semibold text-blue-400 hover:text-blue-300 transition-colors underline"
              >
                Effacer
              </button>
            </div>
          )}
          <FloorPressureOverlay
            run={run}
            outputSummary={outputSummary}
            onCellClick={onCellClick}
            onHotSpotClick={onHotSpotClick}
            embedded={true}
            selectedPressureZone={_selectedPressureZone}
          />
        </div>
      ),
    },
    {
      id: 'score_metrics',
      label: 'Score',
      icon: <ChartBarIcon className="w-4 h-4" />,
      badge: (
        <span className="px-1.5 py-0.5 text-[9px] font-bold rounded-full bg-indigo-500/20 text-indigo-300 border border-indigo-500/30">
          {globalScore == null ? '—' : globalScore.toFixed(0)}/100
        </span>
      ),
      subTabs: [
        {
          id: 'score',
          label: 'Radar Spider',
          content: () => (
            <div className="flex flex-col items-center py-2">
              {outputSummaryUnavailable
                ? <p className="text-xs text-amber-400">Score indisponible : projection du résultat à vérifier.</p>
                : <ScoreRadarChart
                  scoreBreakdown={outputSummary
                    ? { ...outputSummary.optimization_diagnostics.score_breakdown.objectives, weights: outputSummary.optimization_diagnostics.score_breakdown.weights }
                    : run.result?.scoreBreakdown ?? null}
                  globalScore={outputSummary
                    ? outputSummary.optimization_diagnostics.score_breakdown.global_score ?? undefined
                    : run.result?.global_score}
                  className="scale-90 origin-top"
                />}
            </div>
          ),
        },
        {
          id: 'metrics',
          label: 'Métriques Clés',
          content: () => (
            <div className="space-y-3 text-xs">
              <div>
                <div className="flex justify-between mb-1">
                  <span className="text-slate-400">Taux de Remplissage Volume</span>
                  <span className="font-bold text-white">{outputSummaryUnavailable ? '—' : outputSummary
                    ? outputSummary.metrics.fill_rate_volume == null ? '—' : `${(outputSummary.metrics.fill_rate_volume * 100).toFixed(1)}%`
                    : run.result?.fill_rate_volume == null ? '—' : `${(run.result?.fill_rate_volume * 100).toFixed(1)}%`}</span>
                </div>
                <div className="h-1.5 bg-slate-800 rounded-full overflow-hidden">
                  <div className={`h-full ${getFillRateColor(outputSummaryUnavailable ? null : outputSummary?.metrics.fill_rate_volume ?? run.result?.fill_rate_volume ?? null)}`} style={{ width: `${outputSummaryUnavailable ? 0 : Math.min(100, Math.max(0, (outputSummary?.metrics.fill_rate_volume ?? run.result?.fill_rate_volume ?? 0) * 100))}%` }} />
                </div>
              </div>
              {fillRateWeight != null && (
                <div>
                  <div className="flex justify-between mb-1">
                    <span className="text-slate-400">Charge Massique</span>
                    {/* fillRateWeight est déjà en % depuis le Viewer (V01) */}
                    <span className={`font-bold ${fillRateWeight > 100 ? 'text-red-400' : 'text-white'}`}>
                      {fillRateWeight.toFixed(1)}%
                      {fillRateWeight > 100 && <span className="ml-1 text-[10px]">SURCHARGE</span>}
                    </span>
                  </div>
                  <div className="h-1.5 bg-slate-800 rounded-full overflow-hidden">
                    <div
                      className={`h-full ${fillRateWeight > 100 ? 'bg-red-600' : fillRateWeight > 95 ? 'bg-red-500' : fillRateWeight > 80 ? 'bg-amber-500' : 'bg-emerald-500'}`}
                      style={{ width: `${Math.min(100, fillRateWeight)}%` }}
                    />
                  </div>
                </div>
              )}
            </div>
          ),
        },
      ],
    },
    {
      id: 'stability',
      label: 'Stabilité',
      icon: <Scale className="w-4 h-4" />,
      indicatorTone: ds?.overallRiskLevel === 'critical' ? 'danger'
        : ds?.overallRiskLevel === 'high' ? 'warning'
          : ds?.overallRiskLevel === 'moderate' ? 'caution'
            : ds?.overallRiskLevel === 'low' ? 'success' : 'neutral',
      badge: ds ? (() => {
        const riskLevel = ds.overallRiskLevel
        const isOk = riskLevel === 'low'
        const isCritical = riskLevel === 'critical'
        const isHigh = riskLevel === 'high'
        const RiskIcon = isOk ? ShieldCheckIcon : ShieldExclamationIcon
        const riskLabel = isCritical ? 'Critique' : isHigh ? 'Élevé' : riskLevel === 'moderate' ? 'Modéré' : riskLevel === 'low' ? 'Faible' : 'Inconnu'
        const iconColor = isCritical
          ? 'text-red-500'
          : isHigh
            ? 'text-orange-400'
            : riskLevel === 'moderate'
              ? 'text-amber-400'
              : riskLevel === 'low'
                ? 'text-emerald-400'
                : 'text-slate-400'
        return (
          <span title={`Risque d'instabilité : ${riskLabel}`} className="inline-flex items-center">
            <RiskIcon className={`w-3.5 h-3.5 flex-shrink-0 ${iconColor}`} />
          </span>
        )
      })() : undefined,
      content: stabilityIndicatorsContent,
    },
    {
      id: 'alerts',
      label: 'Alertes',
      icon: <ExclamationTriangleIcon className="w-4 h-4" />,
      indicatorTone: violations.length > 0 ? 'danger' : warnings.length > 0 ? 'warning' : 'success',
      badge: (
        <div className="flex items-center gap-1">
          {violations.length > 0 && (
            <span className="px-1.5 py-0.5 text-[9px] font-bold rounded-full bg-red-500 text-white">
              {violations.length}
            </span>
          )}
          {warnings.length > 0 && (
            <span className="px-1.5 py-0.5 text-[9px] font-bold rounded-full bg-amber-500 text-black">
              {groupedWarningCount}
            </span>
          )}
        </div>
      ),
      subTabs: [
        {
          id: 'violations',
          label: `Violations (${violations.length})`,
          content: () => violations.length === 0 ? (
            <div className="text-center py-8">
              <CheckCircleIcon className="w-8 h-8 text-emerald-400 mx-auto mb-2" />
              <p className="text-xs font-semibold text-emerald-400">Aucune violation de contrainte</p>
            </div>
          ) : (
            <div className="space-y-2">
              <p className="text-xs">Consultez les contraintes signalées avant de diffuser le plan.</p>
              {violations.map((violation, index) => (
                <div key={index} className={`rounded-xl border p-3 text-xs ${isDark ? 'border-red-500/20 text-red-300' : 'border-red-200 text-red-800'}`}>
                  <p className="mb-1 text-[10px] font-bold uppercase">{alertSeverity(violation.severity)}</p>
                  <p>{violation.message || violation.type}</p>
                  <details className="mt-2"><summary className="cursor-pointer">Détails de la contrainte</summary>
                    <AlertMeasurement alert={violation} system={run.input.measurement_system ?? 'metric'} />
                  </details>
                  {alertCargoContent(violation)}
                </div>
              ))}
            </div>
          ),
        },
        {
          id: 'warnings',
          label: `Avertissements (${groupedWarningCount})`,
          content: warningsListContent,
        },
      ],
    },
    {
      id: 'diagnostics',
      label: 'Diagnostics',
      icon: <CpuChipIcon className="w-4 h-4" />,
      content: diagnosticsContent,
    }
  ]

  return (
    <>
      {/* Playback Controls (Standalone bottom bar) */}
      {presentation === 'dashboard' && <div className={`viewer-workspace ${sceneOnly ? 'viewer-workspace-scene-only' : ''}`}>
        <section className="viewer-stage" aria-label="Plan de chargement 3D">
          {scene}
          {playback && <div className="relative"><div style={{ visibility: editing ? 'hidden' : 'visible' }}><DashboardPlaybackControls {...playback} isDark={isDark} sequenceProvided={outputSummary?.loading_sequence.status === 'provided'} /></div>{editing && <div className="viewer-edit-tools-slot">{editorToolbar}</div>}</div>}
        </section>
        <div className={sceneOnly ? editing ? 'viewer-floating-editor' : 'hidden' : 'min-w-0'}>
          {editing ? editorPanel : <TabbedOverlayPanel presentation="dashboard" focusRequest={Number(openWarningsPanel) + pressurePanelRequest + violationsPanelRequest + unloadedPanelRequest + diagnosticsPanelRequest}
            tabs={tabsConfig} activeTab={activeTab} onTabChange={setActiveTab} activeSubTab={activeSubTab}
            onSubTabChange={(tab, subTab) => setActiveSubTab(prev => ({ ...prev, [tab]: subTab }))}
            isOpen onToggleOpen={open => setIsPanelOpen(value => open ?? !value)} isDark={isDark} />}
        </div>
      </div>}
      {presentation === 'floating' && playback && (
        <LoadingPlaybackControls
          step={playback.step}
          totalSteps={playback.totalSteps}
          isPlaying={playback.isPlaying}
          speed={playback.speed}
          isAtEnd={playback.isAtEnd}
          isAtStart={playback.isAtStart}
          progress={playback.progress}
          currentItemLabel={playback.currentItemLabel}
          onPlay={playback.onPlay}
          onPause={playback.onPause}
          onReset={playback.onReset}
          onNext={playback.onNext}
          onPrev={playback.onPrev}
          onSpeedChange={playback.onSpeedChange}
          onSeek={playback.onSeek}
          isDark={isDark}
          fullscreen={fullscreen}
        />
      )}

      {/* Main Tabbed Side Panel */}
      {presentation === 'floating' && <Suspense fallback={<div role="status" className="absolute right-4 top-64 rounded-xl bg-slate-900 p-4 text-white">Chargement de l’analyse…</div>}>
      <TabbedOverlayPanel
        tabs={tabsConfig}
        activeTab={activeTab}
        onTabChange={(tabId) => setActiveTab(tabId)}
        activeSubTab={activeSubTab}
        onSubTabChange={(tabId, subTabId) => {
          setActiveSubTab(prev => ({ ...prev, [tabId]: subTabId }))
        }}
        isOpen={isPanelOpen}
        onToggleOpen={(open) => setIsPanelOpen(open ?? !isPanelOpen)}
        isDark={isDark}
      />
      </Suspense>}
      {productFormItem && (
        <ProductForm
          key={productFormItem.id || 'optimization-input-item'}
          product={productFormItem}
          onSave={() => undefined}
          onClose={() => setProductFormItem(null)}
          isLoading={false}
          adrClasses={productFormItem.dangerous_goods_class ? [{
            id: productFormItem.dangerous_goods_class,
            code: productFormItem.dangerous_goods_class,
            label: productFormItem.dangerous_goods_class,
            metadata: { label_short: productFormItem.dangerous_goods_class },
          }] : []}
          unNumbers={productFormItem.un_number ? [{
            id: productFormItem.un_number,
            code: productFormItem.un_number,
            label: productFormItem.un_number,
            metadata: { adr_class: productFormItem.dangerous_goods_class },
          }] : []}
          portal
          readOnly
        />
      )}
    </>
  )
}

// Hook for using overlay state in parent components
export function useOverlayManager() {
  const [activeTab, setActiveTab] = useState('articles')
  const [isPanelOpen, setIsPanelOpen] = useState(true)

  return {
    activeTab,
    setActiveTab,
    isPanelOpen,
    setIsPanelOpen,
  }
}
